import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import AssignNurseModal, { StaffMember, Patient } from '../components/AssignNurseModal';
import AddNurseModal from '../components/AddNurseModal';
import ZoomableImage from '../components/ZoomableImage';
import ViewNurseModal from '../components/ViewNurseModal';
import { DUTY_SHIFTS, shiftsForDuty } from '../constants/shifts';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
} from 'recharts';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// ─── Staff Pool cell helpers (ported from NursesPage) ──────────────────────────
const PLACEHOLDER_IMG = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
const getPoolImageUrl = (path?: string) => path ? (path.startsWith('http') ? path : `${API}/${path}`) : PLACEHOLDER_IMG;
const poolCalcAge = (dob?: string) => {
  if (!dob) return '-';
  const today = new Date(); const b = new Date(dob);
  let age = today.getFullYear() - b.getFullYear();
  const m = today.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--;
  return age;
};
const PoolTick = () => <svg width="15" height="15" fill="none" stroke="#16a34a" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const PoolCross = () => <svg width="15" height="15" fill="none" stroke="#ef4444" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const PoolTC = ({ v }: { v: boolean }) => <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{v ? <PoolTick /> : <PoolCross />}</div>;
const poolQualifications = (qs: any[]) => {
  if (!qs?.length) return <span>-</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      {qs.map((q, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <span style={{ fontWeight: 600 }}>{q.qualification_name}</span>
          {q.institution_name && <span style={{ color: '#64748b', fontSize: '0.67rem' }}>{q.institution_name}</span>}
        </div>
      ))}
    </div>
  );
};
const poolContacts = (nurse: any) => {
  const nums = Array.from(new Set([
    ...(nurse.contacts?.map((c: any) => c.phone_number).filter(Boolean) || []),
    ...(nurse.whatsapp_numbers?.map((w: any) => w.whatsapp_number).filter(Boolean) || []),
  ]));
  if (!nums.length) return <span>-</span>;
  return <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>{nums.map((n: any, i) => <span key={i}>{n}</span>)}</div>;
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface Assignment {
  id: number;
  patient_id?: number;
  patient?: { id: number; patient_name: string };
  nurse?: { id: number; full_name: string };
  physio?: { id: number; full_name: string };
  second_nurse?: { id: number; full_name: string };
  second_physio?: { id: number; full_name: string };
  shift_type: string;
  session?: string;
  start_date: string;
  end_date: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

// Duty type → shift columns now lives in constants/shifts.ts (single source of
// truth, imported above as DUTY_SHIFTS / shiftsForDuty).

// Case status — persisted on the patient (patients.criticality column, reused).
// Two states: Pending (red, needs attention) and Resolved (green).
const CRITICALITY_CFG: Record<string, { bg: string; color: string; border: string; dot: string }> = {
  'Pending':  { bg: '#fecaca', color: '#991b1b', border: '#f87171', dot: '#dc2626' },
  'Resolved': { bg: '#bbf7d0', color: '#166534', border: '#4ade80', dot: '#16a34a' },
};
const CRITICALITY_ORDER: Record<string, number> = { Pending: 0, Resolved: 1 };
const CRITICALITY_OPTIONS = ['Pending', 'Resolved'];
const DEFAULT_CRITICALITY = 'Pending';
// Any legacy value (Critical/High/Moderate/Stable) normalises to Pending.
const normStatus = (v: any): string => (CRITICALITY_OPTIONS.includes(v) ? v : DEFAULT_CRITICALITY);

// VIP client name colour (gold).
const VIP_GOLD = '#b8860b';

// Shift options offered when staging an upcoming case (matches the grid shift types).
const SHIFT_OPTIONS: { type: string; label: string }[] = [
  { type: '08 Morning',   label: '08 Morn' },
  { type: '08 Afternoon', label: '08 Aft' },
  { type: '08 Night',     label: '08 Night' },
  { type: '12 Day',       label: '12 Day' },
  { type: '12 Night',     label: '12 Night' },
  { type: '24 Hrs',       label: '24hr' },
];


// ─── Helpers ──────────────────────────────────────────────────────────────────

const todayStr = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

const fmtDateLong = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
};

const fmtMonthYear = (s: string) => {
  const [y, m] = s.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

// First/last calendar day of the month containing `ymd`, plus the day count.
const monthBounds = (ymd: string) => {
  const [y, m] = ymd.split('-').map(Number);
  const p = (n: number) => String(n).padStart(2, '0');
  const lastDate = new Date(y, m, 0).getDate();
  return { first: `${y}-${p(m)}-01`, last: `${y}-${p(m)}-${p(lastDate)}`, y, m, lastDate };
};

const addDays = (s: string, delta: number) => {
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, m - 1, d + delta);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const fmtDateShort = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
};

const fmtYmd = (x: Date) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;

// Inclusive [start,end] for the Leave Tracker's daily / weekly (Mon–Sun) / monthly view.
const leaveRangeFor = (anchor: string, view: 'daily' | 'weekly' | 'monthly') => {
  const [y, m, d] = anchor.split('-').map(Number);
  if (view === 'daily') return { start: anchor, end: anchor };
  if (view === 'monthly') {
    const start = `${y}-${String(m).padStart(2, '0')}-01`;
    const last = new Date(y, m, 0).getDate();
    return { start, end: `${y}-${String(m).padStart(2, '0')}-${String(last).padStart(2, '0')}` };
  }
  const dow = (new Date(y, m - 1, d).getDay() + 6) % 7; // 0 = Monday
  return { start: fmtYmd(new Date(y, m - 1, d - dow)), end: fmtYmd(new Date(y, m - 1, d - dow + 6)) };
};

const getClientName = (p: Patient): string => {
  const clients = (p as any).patient_clients || (p as any).clients_info || [];
  if (clients.length > 0) return clients[0].client_name || clients[0].name || '';
  return '';
};

// Ops-card note view-box colors.
const NOTE_COLORS: Record<string, string> = {
  black: '#0f172a', blue: '#2563eb', green: '#16a34a', red: '#dc2626', yellow: '#ca8a04',
};

// Build a polished floating "card" as the drag image so the whole card visibly
// lifts and follows the cursor (tilted, shadowed) instead of the default ghost.
const buildLeaveDragImage = (e: React.DragEvent, name: string, sub: string, color: string) => {
  const initial = (name || '?').charAt(0).toUpperCase();
  const ghost = document.createElement('div');
  ghost.style.cssText = [
    'position:fixed', 'top:-1000px', 'left:-1000px', 'width:236px', 'box-sizing:border-box',
    'padding:11px 13px', 'background:#ffffff', `border:1px solid ${color}33`, `border-left:4px solid ${color}`,
    'border-radius:12px', 'box-shadow:0 18px 40px -10px rgba(15,23,42,0.45)',
    'display:flex', 'align-items:center', 'gap:10px', 'transform:rotate(-3deg)',
    "font-family:'Inter',-apple-system,sans-serif",
  ].join(';');
  ghost.innerHTML =
    `<div style="width:32px;height:32px;border-radius:50%;background:${color};color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;flex-shrink:0;">${initial}</div>` +
    `<div style="min-width:0;"><div style="font-size:12.5px;font-weight:700;color:#0f172a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${name || ''}</div>` +
    `<div style="font-size:10px;color:#94a3b8;white-space:nowrap;">${sub || ''}</div></div>`;
  document.body.appendChild(ghost);
  try { e.dataTransfer.setDragImage(ghost, 24, 26); e.dataTransfer.effectAllowed = 'move'; } catch { /* noop */ }
  setTimeout(() => { try { document.body.removeChild(ghost); } catch { /* noop */ } }, 0);
};

const getDutyLabel = (p: Patient): string => {
  if ((p.duty_type === 'Specify' || p.duty_type === 'Other') && (p as any).duty_type_specify)
    return (p as any).duty_type_specify as string;
  return p.duty_type || '—';
};

// Case ordering by duty type: 08 Hrs → 12 Day → 12 Night → 12 Dual → 24 Hrs.
const DUTY_ORDER = ['08 Hrs', '12 Day', '12 Night', '12 Dual', '24 Hrs'];
const dutyRank = (p: Patient): number => {
  const i = DUTY_ORDER.indexOf(getDutyLabel(p));
  return i === -1 ? DUTY_ORDER.length : i;
};

const getDutyShortCode = (dutyType: string): string => {
  if (dutyType.startsWith('08')) return '08';
  if (dutyType.startsWith('12')) return '12';
  if (dutyType.startsWith('24')) return '24';
  return '08';
};

// Reusable searchable dropdown (type to filter; click to pick).
const SearchableSelect: React.FC<{
  value: string;
  display: string;
  placeholder: string;
  options: { id: string; label: string; sub?: string }[];
  onSelect: (id: string) => void;
  allowClear?: boolean;
}> = ({ value, display, placeholder, options, onSelect, allowClear }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const ql = q.trim().toLowerCase();
  const filtered = (ql ? options.filter(o => o.label.toLowerCase().includes(ql) || (o.sub || '').toLowerCase().includes(ql)) : options).slice(0, 50);
  const inputStyle: React.CSSProperties = { width: '100%', marginTop: '4px', padding: '7px 9px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', fontFamily: 'inherit', color: '#0f172a', boxSizing: 'border-box', outline: 'none' };
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        value={open ? q : display}
        placeholder={placeholder}
        onFocus={() => { setOpen(true); setQ(''); }}
        onChange={e => { setQ(e.target.value); if (!open) setOpen(true); }}
        style={inputStyle}
      />
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: '2px', maxHeight: '220px', overflowY: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 12px 28px rgba(15,23,42,0.16)' }}>
          {allowClear && (
            <div onMouseDown={(e) => { e.preventDefault(); onSelect(''); setQ(''); setOpen(false); }}
              style={{ padding: '8px 10px', fontSize: '11px', color: '#94a3b8', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
              — none —
            </div>
          )}
          {filtered.length === 0
            ? <div style={{ padding: '10px', fontSize: '11px', color: '#94a3b8', textAlign: 'center' }}>No match</div>
            : filtered.map(o => (
              <div key={o.id} onMouseDown={(e) => { e.preventDefault(); onSelect(o.id); setQ(''); setOpen(false); }}
                style={{ padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid #f8fafc', background: value === o.id ? '#eff6ff' : 'transparent' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')} onMouseLeave={e => (e.currentTarget.style.background = value === o.id ? '#eff6ff' : 'transparent')}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</div>
                {o.sub && <div style={{ fontSize: '10px', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.sub}</div>}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const OperationsDashboard: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [patients, setPatients] = useState<Patient[]>([]);        // baseline board set (on_ops_board) — used for header stats
  const [allPatients, setAllPatients] = useState<Patient[]>([]);  // full list — needed to resolve per-day added/removed cases
  // Per-DAY board membership overrides, keyed `${patient_id}|${date}` → on_board.
  // A day with no entry falls back to the patient's baseline on_ops_board.
  const [boardOverrides, setBoardOverrides] = useState<Record<string, boolean>>({});
  // Per-DAY case notes, keyed `${patient_id}|${date}` → { note, color }. A day with
  // no entry has no note (blank), so notes never bleed across days.
  const [boardNotes, setBoardNotes] = useState<Record<string, { note: string; color: string }>>({});
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [yesterdayAsgn, setYesterdayAsgn] = useState<Assignment[]>([]);
  const [tomorrowAsgn, setTomorrowAsgn] = useState<Assignment[]>([]);
  // Two independent shift markers, keyed `${patient_id}|${date}|${shift_type}`
  // (covers all 3 visible days). A card shift shows yellow when EITHER is set;
  // the 🚩 button reflects/toggles the manual one only.
  //   manualFlags  → the team pre-warning 🚩 (card only)
  //   autoChanges  → an assignment was actually reassigned (card + grid)
  const [manualFlags, setManualFlags] = useState<Set<string>>(new Set());
  const [autoChanges, setAutoChanges] = useState<Set<string>>(new Set());
  // Per-patient scheduled shift types across the visible month — defines which
  // shift rows a card shows (a shift the patient has appears even on an unstaffed day).
  const [shiftTypesByPatient, setShiftTypesByPatient] = useState<Record<number, string[]>>({});
  // Per-patient CONFIGURED shift columns (persisted shift_order) — the authoritative
  // "which shifts does this case have" set the grid maintains. Takes precedence so a
  // removed column (e.g. afternoon) stays gone on the card even with no assignments.
  const [shiftPrefsByPatient, setShiftPrefsByPatient] = useState<Record<number, string[]>>({});
  const [dayView, setDayView] = useState<'yesterday' | 'today' | 'tomorrow'>('today');
  // Tomorrow-planning: drag a Today card up into the Tomorrow section to copy its staffing.
  const [dragTodayId, setDragTodayId] = useState<number | null>(null);
  const [tmrwOver, setTmrwOver] = useState(false);
  const [carriedIds, setCarriedIds] = useState<Set<number>>(new Set());
  // Today-source suggestions the planner dismissed ("no changes") — hidden from the
  // Tomorrow view's Today strip only; today's real card is untouched.
  const [hiddenTodaySrcIds, setHiddenTodaySrcIds] = useState<Set<number>>(new Set());
  // Collapsible "Yesterday" reference strip in the Today view (default hidden).
  const [showYesterday, setShowYesterday] = useState(false);
  // Yesterday reference cards the user hid (× on the card) — hidden only, not deleted.
  const [hiddenYesterdayIds, setHiddenYesterdayIds] = useState<Set<number>>(new Set());
  const [nurses, setNurses] = useState<StaffMember[]>([]);
  const [physios, setPhysios] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [initialSlots, setInitialSlots] = useState<{ date: string; shiftId: string; session: string }[]>([]);
  const [preNurseId, setPreNurseId] = useState<string | undefined>(undefined);

  // Available Today assign modal
  const [availModal, setAvailModal] = useState(false);
  const [availNurse, setAvailNurse] = useState<{ id: number; name: string } | null>(null);
  const [availPatientId, setAvailPatientId] = useState<string>('');
  const [availShift, setAvailShift] = useState<{ label: string; session: string } | null>(null);
  const [availClientSearch, setAvailClientSearch] = useState('');   // search a case by client (then patient) name

  const [statusMenuId, setStatusMenuId] = useState<number | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const statusMenuRef = useRef<HTMLDivElement | null>(null);
  // Staff Pool "Status" pill → click to change availability (portal dropdown).
  const [availMenuId, setAvailMenuId] = useState<number | null>(null);
  const [availMenuPos, setAvailMenuPos] = useState<{ top: number; left: number } | null>(null);
  const availMenuRef = useRef<HTMLDivElement | null>(null);
  const availAnchorRef = useRef<HTMLElement | null>(null);   // the Status pill the dropdown is glued to
  // Staff Pool: search by name + sort mode
  const [poolSearch, setPoolSearch] = useState('');
  const [poolSort, setPoolSort] = useState<'status' | 'name'>('status');

  // Add case flow
  const [addingCase, setAddingCase] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  // Per-day case note — modal + draft. noteDate is which day's note is being edited.
  const [notePatient, setNotePatient] = useState<Patient | null>(null);
  const [noteDate, setNoteDate] = useState<string>('');
  const [noteDraft, setNoteDraft] = useState('');
  const [noteColorDraft, setNoteColorDraft] = useState('black');
  const [noteSaving, setNoteSaving] = useState(false);
  const [allPatientsList, setAllPatientsList] = useState<Patient[]>([]);

  // ── Upcoming-case staging card ──
  const [ucName, setUcName] = useState('');
  const [ucResults, setUcResults] = useState<Patient[]>([]);   // live client-search results
  const [ucSelected, setUcSelected] = useState<Patient | null>(null);
  const [ucStartDate, setUcStartDate] = useState('');
  const [ucShifts, setUcShifts] = useState<string[]>([]);
  const [ucSaving, setUcSaving] = useState(false);
  const [upcomingAsgn, setUpcomingAsgn] = useState<Assignment[]>([]);  // assignments across the forward window (for upcoming-case staffing)
  const [upcomingFlags, setUpcomingFlags] = useState<Set<string>>(new Set());  // manual 🚩 markers on the forward window (patient|date|shift)
  const [openAvailCat, setOpenAvailCat] = useState<string | null>(null);  // which availability section is popped open (accordion overlay)

  // Availability (per-date, from nurse_availability) + dropdown options (ops_availability ref)
  const [nurseAvail, setNurseAvail] = useState<any[]>([]);          // rows for activeDate
  const [availOptions, setAvailOptions] = useState<string[]>([]);   // the configurable category list
  const [newOptDraft, setNewOptDraft] = useState('');               // "add new option" input
  const [showAddOpt, setShowAddOpt] = useState(false);              // reveal the add-option input
  const [boardSearch, setBoardSearch] = useState('');               // filter case cards by client/patient
  const [availSearch, setAvailSearch] = useState('');               // filter availability list by nurse name

  // Active tab
  const [activeTab, setActiveTab] = useState<'board' | 'leave' | 'upcoming' | 'pool' | 'feedback' | 'log'>('board');

  // Staff Pool → view nurse profile
  const [viewNurseId, setViewNurseId] = useState<number | null>(null);

  // Feedback ("Client Feedback & Ratings")
  const [feedback, setFeedback] = useState<any[]>([]);
  const [fbModal, setFbModal] = useState(false);
  const [fbAllPatients, setFbAllPatients] = useState<Patient[]>([]);
  const [fbSearch, setFbSearch] = useState('');   // filter feedback by CLIENT name
  const [fbForm, setFbForm] = useState<{ staff_type: 'nurse' | 'physio'; staff_id: string; patient_id: string; comment: string; feedback_date: string }>(
    { staff_type: 'nurse', staff_id: '', patient_id: '', comment: '', feedback_date: todayStr() }
  );

  // Leave tracker (kanban over leave availability categories) — drag & drop
  const [dragCard, setDragCard] = useState<any | null>(null);
  const [hoverCol, setHoverCol] = useState<string | null>(null);
  const [leaveModal, setLeaveModal] = useState(false);
  const [leaveForm, setLeaveForm] = useState<{ nurse_id: string; category: string; from_date: string; to_date: string }>({ nurse_id: '', category: 'On Leave - Regular', from_date: '', to_date: '' });
  // Leave-tracker date-range view + leave periods over that range + edit target
  const [leaveView, setLeaveView] = useState<'daily' | 'weekly' | 'monthly'>('monthly');
  const [leavePeriods, setLeavePeriods] = useState<any[]>([]);
  const [leaveEdit, setLeaveEdit] = useState<{ nurse_id: number; from_date: string; to_date: string } | null>(null);
  const [isAddNurseOpen, setIsAddNurseOpen] = useState(false);  // quick-add nurse (New Joinee column)

  // Daily-sales month chart (opened from the Daily Sales stat)
  const [salesModalOpen, setSalesModalOpen] = useState(false);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesData, setSalesData] = useState<{ label: string; sales: number }[]>([]);
  const salesChartRef = useRef<HTMLDivElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);  // hidden native date picker behind the header date label

  // Outsourcing entries — a per-day cost (rate) that is subtracted from daily sales.
  type OutRow = { id: number; date: string; client_name: string; note?: string | null; rate: number | string };
  const [outsourcing, setOutsourcing] = useState<OutRow[]>([]);          // loaded for the yesterday..tomorrow window
  const [outModal, setOutModal] = useState(false);
  const [outForm, setOutForm] = useState<{ client_name: string; note: string; rate: string }>({ client_name: '', note: '', rate: '' });
  const [outClientOpen, setOutClientOpen] = useState(false);            // client suggestions dropdown open

  const token = localStorage.getItem('accessToken');
  const h = token ? { Authorization: `Bearer ${token}` } : {};

  const DEFAULT_AVAIL = 'Available';

  // Availability statuses that block assignment (warn before assigning such staff).
  // Statuses that persist until explicitly changed (backend: open-ended row, no
  // daily re-stamping). Matches app/api/nurse_availability.py PERMANENT_STATUSES.
  const PERMANENT_AVAIL_STATUSES = ['Resigned', 'Absconded', 'Uncertainty'];
  // Also blocks assignment — with, arguably, more reason to stop and check than
  // a leave: assigning a Resigned/Absconded nurse to a real case is a serious
  // mistake, not just a scheduling inconvenience.
  const BLOCKED_AVAIL_STATUSES = ['On Leave - Emergency', 'On Leave - Sick', 'On Leave - Regular', 'On Temporary Duty', ...PERMANENT_AVAIL_STATUSES];

  // ── Fetch ────────────────────────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const yesterdayStr = addDays(selectedDate, -1);
      const tomorrowStr  = addDays(selectedDate, 1);

      // Month window (extended to cover the yesterday/tomorrow views at month edges)
      // — used to derive each patient's scheduled shift types (the card row set).
      const [yy, mm] = selectedDate.split('-').map(Number);
      const monthStart = `${yy}-${String(mm).padStart(2, '0')}-01`;
      const lastDay = new Date(yy, mm, 0).getDate();
      const monthEnd = `${yy}-${String(mm).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      const rangeStart = yesterdayStr < monthStart ? yesterdayStr : monthStart;
      const rangeEnd   = tomorrowStr  > monthEnd   ? tomorrowStr  : monthEnd;

      const [pRes, aRes, yRes, tRes, nRes, phRes, mRes, ppRes, fRes, ovRes, outRes, noteRes] = await Promise.all([
        axios.get(`${API}/api/patients/?limit=300`, { headers: h }),
        axios.get(`${API}/api/assignments`, { headers: h, params: { start_date: selectedDate, end_date: selectedDate } }),
        axios.get(`${API}/api/assignments`, { headers: h, params: { start_date: yesterdayStr, end_date: yesterdayStr } }),
        axios.get(`${API}/api/assignments`, { headers: h, params: { start_date: tomorrowStr, end_date: tomorrowStr } }),
        axios.get(`${API}/api/nurses/`, { headers: h }),
        axios.get(`${API}/api/physiotherapists/`, { headers: h }),
        axios.get(`${API}/api/assignments`, { headers: h, params: { start_date: rangeStart, end_date: rangeEnd } }),
        axios.get(`${API}/api/patients/shift-preferences-all`, { headers: h }).catch(() => ({ data: {} })),
        axios.get(`${API}/api/schedule/flags`, { headers: h, params: { start: yesterdayStr, end: tomorrowStr } }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/ops-board/overrides`, { headers: h, params: { start: yesterdayStr, end: tomorrowStr } }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/outsourcing`, { headers: h, params: { start: yesterdayStr, end: tomorrowStr } }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/ops-board/notes`, { headers: h, params: { start: yesterdayStr, end: tomorrowStr } }).catch(() => ({ data: [] })),
      ]);
      // Full list — the per-day board sets are derived from this + the overrides.
      setAllPatients(pRes.data || []);
      // Baseline board membership (on_ops_board) — kept for header stats. Per-day
      // add/remove is layered on top via boardOverrides.
      const onBoard = (pRes.data || []).filter((p: Patient) => (p as any).on_ops_board !== false);
      setPatients(onBoard);
      // Per-day board overrides → `${patient_id}|${date}` → on_board.
      const ovMap: Record<string, boolean> = {};
      (ovRes.data || []).forEach((o: any) => { ovMap[`${o.patient_id}|${String(o.date).split('T')[0]}`] = o.on_board; });
      setBoardOverrides(ovMap);
      // Per-day notes → `${patient_id}|${date}` → { note, color }.
      const noteMap: Record<string, { note: string; color: string }> = {};
      (noteRes.data || []).forEach((n: any) => {
        noteMap[`${n.patient_id}|${String(n.date).split('T')[0]}`] = { note: n.note || '', color: n.color || 'black' };
      });
      setBoardNotes(noteMap);
      setOutsourcing((outRes.data || []).map((o: any) => ({ ...o, date: String(o.date).split('T')[0] })));
      setAssignments(aRes.data || []);
      setYesterdayAsgn(yRes.data || []);
      setTomorrowAsgn(tRes.data || []);

      // Build each patient's scheduled shift-type set (ordered by first appearance).
      const byPat: Record<number, string[]> = {};
      (mRes.data || []).forEach((a: Assignment) => {
        const pid = a.patient_id ?? a.patient?.id;
        if (pid == null) return;
        if (!byPat[pid]) byPat[pid] = [];
        if (!byPat[pid].includes(a.shift_type)) byPat[pid].push(a.shift_type);
      });
      setShiftTypesByPatient(byPat);

      // Configured shift columns per patient (persisted shift_order) — distinct types,
      // in saved order, dropping placeholders.
      const prefsRaw: Record<string, any[]> = ppRes.data || {};
      const prefs: Record<number, string[]> = {};
      Object.keys(prefsRaw).forEach(k => {
        const arr = Array.isArray(prefsRaw[k]) ? prefsRaw[k] : [];
        const ts: string[] = [];
        arr.forEach((s: any) => {
          const t = s?.type;
          if (t && t !== 'Select Shift...' && !ts.includes(t)) ts.push(t);
        });
        prefs[Number(k)] = ts;
      });
      setShiftPrefsByPatient(prefs);
      setNurses((nRes.data || []).map((n: any) => ({ ...n, staff_type: 'nurse' })));
      setPhysios((phRes.data || []).map((p: any) => ({ ...p, staff_type: 'physio' })));

      // Shift markers (yellow), keyed patient|date|shift_type — manual vs actual change.
      const flagRows: any[] = fRes.data || [];
      const mk = (f: any) => `${f.patient_id}|${String(f.date).split('T')[0]}|${f.shift_type}`;
      setManualFlags(new Set(flagRows.filter(f => f.manual_flag).map(mk)));
      setAutoChanges(new Set(flagRows.filter(f => f.auto_changed).map(mk)));
    } catch (e) {
      console.error('OperationsDashboard fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Feedback ───────────────────────────────────────────────────────────────
  const fetchFeedback = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/staff-feedback`, { headers: h });
      setFeedback(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error('feedback fetch error', e); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

  // Load the full patient list when the Feedback tab opens (needed to resolve client names).
  useEffect(() => {
    if (activeTab === 'feedback' && fbAllPatients.length === 0) {
      axios.get(`${API}/api/patients/?limit=300`, { headers: h })
        .then(res => setFbAllPatients((res.data as Patient[]) || []))
        .catch(e => console.error('load patients for feedback', e));
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const openFeedbackModal = async () => {
    setFbForm({ staff_type: 'nurse', staff_id: '', patient_id: '', comment: '', feedback_date: todayStr() });
    setFbModal(true);
    if (fbAllPatients.length === 0) {
      try {
        const res = await axios.get(`${API}/api/patients/?limit=300`, { headers: h });
        setFbAllPatients(res.data || []);
      } catch (e) { console.error(e); }
    }
  };

  const submitFeedback = async () => {
    if (!fbForm.staff_id) { alert('Select a staff member'); return; }
    if (!fbForm.comment.trim()) { alert('Enter a comment'); return; }
    try {
      await axios.post(`${API}/api/staff-feedback`, {
        staff_id: Number(fbForm.staff_id),
        staff_type: fbForm.staff_type,
        patient_id: fbForm.patient_id ? Number(fbForm.patient_id) : null,
        comment: fbForm.comment.trim(),
        feedback_date: fbForm.feedback_date || null,
      }, { headers: h });
      setFbModal(false);
      fetchFeedback();
    } catch (e) { console.error(e); alert('Failed to save feedback'); }
  };

  const deleteFeedback = async (id: number) => {
    if (!window.confirm('Delete this feedback entry?')) return;
    try {
      await axios.delete(`${API}/api/staff-feedback/${id}`, { headers: h });
      fetchFeedback();
    } catch (e) { console.error(e); }
  };

  // Delete a whole feedback card (all of a staff member's entries).
  const deleteFeedbackCard = async (card: any) => {
    const n = card.entries?.length || 0;
    if (!window.confirm(`Delete all ${n} feedback entr${n === 1 ? 'y' : 'ies'} for ${card.staff_name || 'this staff'}?`)) return;
    try {
      await Promise.all((card.entries || []).map((e: any) => axios.delete(`${API}/api/staff-feedback/${e.id}`, { headers: h })));
      fetchFeedback();
    } catch (e) { console.error(e); }
  };

  const setStaffRating = async (staff_id: number, staff_type: string, rating: number | null) => {
    try {
      await axios.patch(`${API}/api/staff-feedback/rating`, { staff_id, staff_type, rating }, { headers: h });
      fetchFeedback();
      fetchAll();   // refresh nurse/physio records so the Staff Pool rating updates too
    } catch (e) { console.error(e); }
  };

  // Toggle a staff member's VIP flag (persisted on the nurse/physio record).
  const setStaffVip = async (staff_id: number, staff_type: string, is_vip: boolean) => {
    try {
      await axios.patch(`${API}/api/staff-feedback/vip`, { staff_id, staff_type, is_vip }, { headers: h });
      fetchFeedback();
      fetchAll();
    } catch (e) { console.error(e); }
  };

  const fbInput: React.CSSProperties = { width: '100%', marginTop: '4px', padding: '7px 9px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', fontFamily: 'inherit', color: '#0f172a', boxSizing: 'border-box' };

  // Availability category options (configurable reference list: ops_availability)
  const loadAvailOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/reference/config`, { headers: h });
      const opts = (res.data?.config?.ops_availability || []).map((o: any) => o.label).filter(Boolean);
      setAvailOptions(opts.length ? opts : [DEFAULT_AVAIL]);
    } catch (e) { console.error('load avail options', e); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadAvailOptions(); }, [loadAvailOptions]);

  // Effective availability for the active day (Yesterday/Today/Tomorrow)
  const dayDelta = dayView === 'yesterday' ? -1 : dayView === 'tomorrow' ? 1 : 0;
  const availDate = addDays(selectedDate, dayDelta);
  const fetchAvailability = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/nurse-availability`, { headers: h, params: { date: availDate } });
      setNurseAvail(res.data || []);
    } catch (e) { console.error('fetch availability', e); }
  }, [availDate]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAvailability(); }, [fetchAvailability]);

  // Leave periods over the selected daily/weekly/monthly range (for the Leave Tracker).
  const fetchLeavePeriods = useCallback(async () => {
    const { start, end } = leaveRangeFor(availDate, leaveView);
    try {
      const res = await axios.get(`${API}/api/nurse-availability/range`, { headers: h, params: { start, end } });
      setLeavePeriods(res.data || []);
    } catch (e) { console.error('fetch leave periods', e); }
  }, [availDate, leaveView]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLeavePeriods(); }, [fetchLeavePeriods]);

  // Close status menu on outside click
  useEffect(() => {
    if (statusMenuId === null) return;
    const close = (e: MouseEvent) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(e.target as Node)) {
        setStatusMenuId(null);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [statusMenuId]);

  // Close the Staff Pool availability menu on outside click
  useEffect(() => {
    if (availMenuId === null) return;
    const close = (e: MouseEvent) => {
      if (availMenuRef.current && !availMenuRef.current.contains(e.target as Node)) setAvailMenuId(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [availMenuId]);

  // Keep the Staff Pool availability dropdown glued to its Status pill while the
  // page/table scrolls (recompute from the anchor; close if it scrolls off-screen).
  useEffect(() => {
    if (availMenuId === null) return;
    const reposition = () => {
      const el = availAnchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) { setAvailMenuId(null); return; }
      setAvailMenuPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left, window.innerWidth - 186)) });
    };
    window.addEventListener('scroll', reposition, true);  // capture = catches inner scroll containers too
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [availMenuId]);

  // Close the availability section overlay on outside click
  const availIndexRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (openAvailCat === null) return;
    const close = (e: MouseEvent) => {
      if (availIndexRef.current && !availIndexRef.current.contains(e.target as Node)) {
        setOpenAvailCat(null);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openAvailCat]);

  // ── Date navigation ──────────────────────────────────────────────────────────

  const changeDay = (delta: number) => {
    const [y, m, d] = selectedDate.split('-').map(Number);
    const next = new Date(y, m - 1, d + delta);
    setSelectedDate(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`);
  };

  // ── Daily-sales month chart ──────────────────────────────────────────────────
  // Fetch every assignment in the current month, sum the client charge per day
  // (primary + assistant), and fill a bar for each calendar day (0 where none).
  const openDailySalesChart = async () => {
    setSalesModalOpen(true);
    setSalesLoading(true);
    try {
      const { first, last, y, m, lastDate } = monthBounds(selectedDate);
      const [res, outRes] = await Promise.all([
        // limit:0 = no cap (endpoint returns all). The default 1000 (sorted by date)
        // otherwise truncates the month to its first ~12 days, dropping the rest.
        axios.get(`${API}/api/assignments`, { headers: h, params: { start_date: first, end_date: last, limit: 0 } }),
        // Outsourcing for the month — subtracted per day so the chart matches the NET header.
        axios.get(`${API}/api/outsourcing`, { headers: h, params: { start: first, end: last } }).catch(() => ({ data: [] })),
      ]);
      const rows: any[] = res.data || [];
      const byDay: Record<string, number> = {};
      rows.forEach(a => {
        const d = String(a.start_date).slice(0, 10);
        byDay[d] = (byDay[d] || 0) + (Number(a.total_charge) || 0) + (Number(a.second_total_charge) || 0);
      });
      (outRes.data || []).forEach((o: any) => {
        const d = String(o.date).slice(0, 10);
        byDay[d] = (byDay[d] || 0) - (Number(o.rate) || 0);
      });
      const monthShort = new Date(y, m - 1, 1).toLocaleString('en-GB', { month: 'short' });
      const p = (n: number) => String(n).padStart(2, '0');
      setSalesData(Array.from({ length: lastDate }, (_, i) => {
        const day = i + 1;
        return { label: `${day} ${monthShort}`, sales: Math.round(byDay[`${y}-${p(m)}-${p(day)}`] || 0) };
      }));
    } catch (e) {
      console.error('daily sales chart fetch', e);
      setSalesData([]);
    } finally {
      setSalesLoading(false);
    }
  };

  // Export the rendered chart SVG to a PNG the user can save.
  const downloadSalesChart = () => {
    const svg = salesChartRef.current?.querySelector('svg');
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const w = Math.round(rect.width) || 900;
    const hgt = Math.round(rect.height) || 440;
    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute('width', String(w));
    clone.setAttribute('height', String(hgt));
    const xml = new XMLSerializer().serializeToString(clone);
    const url = URL.createObjectURL(new Blob([xml], { type: 'image/svg+xml;charset=utf-8' }));
    const img = new Image();
    img.onload = () => {
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width = w * scale; canvas.height = hgt * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) { URL.revokeObjectURL(url); return; }
      ctx.scale(scale, scale);
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, w, hgt);
      ctx.drawImage(img, 0, 0, w, hgt);
      URL.revokeObjectURL(url);
      canvas.toBlob(blob => {
        if (!blob) return;
        const dl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = dl; a.download = `daily-sales-${selectedDate.slice(0, 7)}.png`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(dl);
      }, 'image/png');
    };
    img.src = url;
  };

  // ── Assignment helpers ───────────────────────────────────────────────────────

  // Builds the shift rows for a patient. The ROW SET is the patient's scheduled
  // shift types across the month (shiftTypesByPatient) — so a shift the patient has
  // shows even on a day it isn't staffed yet (as an open "+ Assign" slot), and a
  // shift deleted from the grid (gone every day) disappears. The ACTIVE DAY's
  // assignment list only decides who fills each row.
  const getShiftsForPatient = (patient: Patient, list: Assignment[] = assignments) => {
    const dutyType = getDutyLabel(patient);
    const shiftDefs = shiftsForDuty(patient.duty_type || dutyType);
    const patAsgn  = list.filter(a => (a.patient_id ?? a.patient?.id) === patient.id);

    const namesOf = (a?: Assignment) => {
      const names: string[] = [];
      if (a?.nurse?.full_name) names.push(a.nurse.full_name);
      else if (a?.physio?.full_name) names.push(a.physio.full_name);
      if (a?.second_nurse?.full_name) names.push(a.second_nurse.full_name);
      else if (a?.second_physio?.full_name) names.push(a.second_physio.full_name);
      return names;
    };

    // column id matches the grid's column key: type lowercased, spaces -> "_".
    const colId = (t: string) => t.toLowerCase().replace(/\s+/g, '_');
    const labelFor = (type: string, sess?: string) =>
      shiftDefs.find(d => d.type === type || (sess && d.session === sess) || d.label === type)?.label ?? type;

    // Row set priority:
    //  1) the patient's CONFIGURED shift columns (persisted shift_order) — so a
    //     removed column (e.g. afternoon) stays gone even with no assignments;
    //  2) else the shift types scheduled across the month;
    //  3) else types present in the active day's list (just after an add);
    //  4) else the duty-type template (brand-new case with no config at all).
    const pref = shiftPrefsByPatient[patient.id];
    let types = (pref && pref.length > 0)
      ? pref
      : (shiftTypesByPatient[patient.id] ?? Array.from(new Set(patAsgn.map(a => a.shift_type))));
    if (types.length === 0) types = shiftDefs.map(d => d.type);

    const slots = types.map(type => {
      const a = patAsgn.find(x => x.shift_type === type);
      return {
        label: labelFor(type, a?.session),
        shiftId: colId(type),
        session: type,
        names: a ? namesOf(a) : [],
        hasAssignment: !!a,
        isCarried: false,
        carriedNurseId: undefined as string | undefined,
      };
    });

    // Natural duty order (Morning -> Afternoon -> Night), unknowns last.
    const order = (s: { session: string }) => {
      const i = shiftDefs.findIndex(d => d.type === s.session || d.session === s.session);
      return i === -1 ? 99 : i;
    };
    slots.sort((a, b) => order(a) - order(b));

    return slots;
  };

  // ── Stats ────────────────────────────────────────────────────────────────────

  const totalOpenSlots = patients.reduce((acc, p) => {
    return acc + getShiftsForPatient(p).filter(s => s.names.length === 0).length;
  }, 0);

  // ── Day view (Yesterday | Today | Tomorrow), relative to the header-anchored date ──
  const activeDate = dayView === 'yesterday' ? addDays(selectedDate, -1)
                   : dayView === 'tomorrow'  ? addDays(selectedDate, 1)
                   : selectedDate;
  const activeAssignments = dayView === 'yesterday' ? yesterdayAsgn
                          : dayView === 'tomorrow'  ? tomorrowAsgn
                          : assignments;
  const isEditable = dayView !== 'yesterday';  // yesterday is read-only

  // ── Shift change flags (yellow "change likely / changed" marker on cards) ──────
  const flagKey = (patientId: number, date: string, shiftType: string) => `${patientId}|${date}|${shiftType}`;

  // Toggle the MANUAL 🚩 only (never touches the auto "changed" marker / grid).
  const toggleShiftFlag = async (patient: Patient, shiftType: string) => {
    const key = flagKey(patient.id, activeDate, shiftType);
    const wasManual = manualFlags.has(key);
    // Optimistic toggle of the manual layer
    setManualFlags(prev => { const n = new Set(prev); wasManual ? n.delete(key) : n.add(key); return n; });
    try {
      if (wasManual) {
        await axios.delete(`${API}/api/schedule/flags`, { headers: h, params: { patient_id: patient.id, date: activeDate, shift_type: shiftType } });
      } else {
        await axios.post(`${API}/api/schedule/flags`, { patient_id: patient.id, date: activeDate, shift_type: shiftType }, { headers: h });
      }
    } catch (e) {
      console.error('toggle shift flag failed', e);
      // Revert on failure
      setManualFlags(prev => { const n = new Set(prev); wasManual ? n.add(key) : n.delete(key); return n; });
    }
  };


  // ── Add case search ──────────────────────────────────────────────────────────

  const searchAllPatients = async (q: string) => {
    setAddSearch(q);
    if (q.length < 2) { setAllPatientsList([]); return; }
    try {
      const res = await axios.get(`${API}/api/patients/?limit=300`, { headers: h });
      // Offer any case NOT on the ACTIVE DAY's board (so a case shown on another
      // day can still be added to this one).
      const onBoardIds = new Set((res.data || []).filter((p: Patient) => isOnBoardForDay(p, activeDate)).map((p: Patient) => p.id));
      setAllPatientsList(
        (res.data || []).filter((p: Patient) =>
          !onBoardIds.has(p.id) &&
          ((p.patient_name || '').toLowerCase().includes(q.toLowerCase()) ||
           getClientName(p).toLowerCase().includes(q.toLowerCase()))
        )
      );
    } catch (e) { console.error(e); }
  };

  // Add a case to the board for the ACTIVE DAY ONLY (per-day override). The other
  // days are untouched; the case stays as-is in the client list (status untouched).
  const addPatientToBoard = async (p: Patient) => {
    // A change is logged for the ACTIVE DAY ONLY. Each day's board is independent —
    // adding on Today never seeds Tomorrow (Tomorrow is planned separately).
    const key = `${p.id}|${activeDate}`;
    try {
      // Make sure this patient object is available to render (may be off-baseline).
      setAllPatients(prev => (prev.some(x => x.id === p.id) ? prev : [...prev, p]));
      setBoardOverrides(prev => ({ ...prev, [key]: true }));  // optimistic
      setAddingCase(false);
      setAddSearch('');
      setAllPatientsList([]);
      await axios.post(`${API}/api/ops-board/override`, { patient_id: p.id, date: activeDate, on_board: true }, { headers: h });
    } catch (e) {
      console.error(e);
      setBoardOverrides(prev => { const n = { ...prev }; delete n[key]; return n; });  // revert
    }
  };

  // Remove a case from the board for the ACTIVE DAY ONLY. Yesterday / Today /
  // Tomorrow are independent — removing here never affects the other days.
  const removePatientFromBoard = async (p: Patient) => {
    if (!window.confirm(`Remove ${getClientName(p) || p.patient_name} from ${dayView === 'yesterday' ? "yesterday's" : dayView === 'tomorrow' ? "tomorrow's" : "today's"} board? Other days are unaffected — you can add it back anytime.`)) return;
    const key = `${p.id}|${activeDate}`;
    const prevVal = boardOverrides[key];
    setBoardOverrides(prev => ({ ...prev, [key]: false }));  // optimistic
    try {
      await axios.post(`${API}/api/ops-board/override`, { patient_id: p.id, date: activeDate, on_board: false }, { headers: h });
    } catch (e) {
      console.error('Failed to remove case', e);
      setBoardOverrides(prev => { const n = { ...prev }; if (prevVal === undefined) delete n[key]; else n[key] = prevVal; return n; });  // revert
    }
  };

  // ── Outsourcing (per-day cost, subtracted from daily sales) ──────────────────
  const openOutsourcing = () => {
    setOutForm({ client_name: '', note: '', rate: '' });
    setOutClientOpen(false);
    setOutModal(true);
  };

  const saveOutsourcing = async () => {
    const rate = parseFloat(outForm.rate);
    if (!outForm.client_name.trim()) { window.alert('Enter or pick a client name.'); return; }
    if (isNaN(rate) || rate < 0) { window.alert('Enter a valid rate.'); return; }
    try {
      const res = await axios.post(`${API}/api/outsourcing`, {
        date: activeDate,
        client_name: outForm.client_name.trim(),
        note: outForm.note.trim() || null,
        rate,
      }, { headers: h });
      setOutsourcing(prev => [...prev, { ...res.data, date: String(res.data.date).split('T')[0] }]);
      setOutModal(false);
    } catch (e) {
      console.error('save outsourcing', e);
      window.alert('Failed to save outsourcing entry.');
    }
  };

  const deleteOutsourcing = async (id: number) => {
    if (!window.confirm('Remove this outsourcing entry?')) return;
    try {
      await axios.delete(`${API}/api/outsourcing/${id}`, { headers: h });
      setOutsourcing(prev => prev.filter(o => o.id !== id));
    } catch (e) {
      console.error('delete outsourcing', e);
      window.alert('Failed to remove outsourcing entry.');
    }
  };

  // ── Upcoming-case staging ────────────────────────────────────────────────────

  // Assignments across a forward window so upcoming cards show real staffing for their
  // (future) start dates, not today's.
  const fetchUpcomingAsgn = useCallback(async () => {
    try {
      const end = addDays(selectedDate, 90);
      const [res, fRes, nRes] = await Promise.all([
        axios.get(`${API}/api/assignments`, { headers: h, params: { start_date: selectedDate, end_date: end } }),
        // Manual 🚩 markers across the forward window (so upcoming cards show their own flags).
        axios.get(`${API}/api/schedule/flags`, { headers: h, params: { start: selectedDate, end } }).catch(() => ({ data: [] })),
        // Per-day notes across the forward window (so upcoming cards show their own notes).
        axios.get(`${API}/api/ops-board/notes`, { headers: h, params: { start: selectedDate, end } }).catch(() => ({ data: [] })),
      ]);
      setUpcomingAsgn(res.data || []);
      const mk = (f: any) => `${f.patient_id}|${String(f.date).split('T')[0]}|${f.shift_type}`;
      setUpcomingFlags(new Set((fRes.data || []).filter((f: any) => f.manual_flag).map(mk)));
      // Merge forward-window notes into the shared map (don't wipe the daily-board window).
      setBoardNotes(prev => {
        const next = { ...prev };
        (nRes.data || []).forEach((n: any) => { next[`${n.patient_id}|${String(n.date).split('T')[0]}`] = { note: n.note || '', color: n.color || 'black' }; });
        return next;
      });
    } catch (e) { console.error('fetch upcoming assignments', e); }
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab === 'upcoming') fetchUpcomingAsgn(); }, [activeTab, fetchUpcomingAsgn]);

  // Date-aware manual 🚩 toggle for upcoming cards. Unlike the Daily Board's
  // toggleShiftFlag (which is hardwired to activeDate), this flags the case's own
  // future start date so the marker lands on the right day.
  const toggleUpcomingFlag = async (patient: Patient, shiftType: string, date: string) => {
    const key = flagKey(patient.id, date, shiftType);
    const wasFlagged = upcomingFlags.has(key);
    setUpcomingFlags(prev => { const n = new Set(prev); wasFlagged ? n.delete(key) : n.add(key); return n; }); // optimistic
    try {
      if (wasFlagged) {
        await axios.delete(`${API}/api/schedule/flags`, { headers: h, params: { patient_id: patient.id, date, shift_type: shiftType } });
      } else {
        await axios.post(`${API}/api/schedule/flags`, { patient_id: patient.id, date, shift_type: shiftType }, { headers: h });
      }
    } catch (e) {
      console.error('toggle upcoming flag failed', e);
      setUpcomingFlags(prev => { const n = new Set(prev); wasFlagged ? n.add(key) : n.delete(key); return n; }); // revert
    }
  };

  const ucReset = () => { setUcName(''); setUcResults([]); setUcSelected(null); setUcShifts([]); setUcStartDate(''); };

  // Does this patient/client match the query (patient name OR any client name)?
  const ucNameMatches = (p: any, q: string): boolean => {
    if ((p.patient_name || '').toLowerCase().includes(q)) return true;
    if ((p.client_name || '').toLowerCase().includes(q)) return true;
    const clients = p.patient_clients || p.clients_info || [];
    return clients.some((c: any) => (c.client_name || c.name || '').toLowerCase().includes(q));
  };

  // Live client search (fresh from the API on every keystroke — like the Daily Board),
  // so existing and just-added clients both resolve without a page refresh.
  const ucSearch = async (raw: string) => {
    setUcName(raw);
    if (ucSelected) setUcSelected(null);
    const q = raw.trim().toLowerCase();
    if (q.length < 2) { setUcResults([]); return; }
    try {
      const res = await axios.get(`${API}/api/patients/?limit=300`, { headers: h });
      setUcResults((res.data || []).filter((p: Patient) => ucNameMatches(p, q)).slice(0, 8));
    } catch (e) { console.error('upcoming client search', e); setUcResults([]); }
  };

  // Pick an existing client → prefill their previous shift setup. Authoritative source is
  // the client's saved shift columns; fall back to their duty type so a client with a
  // known service (e.g. 24 Hrs) still prefills even without a saved schedule.
  const ucSelectClient = async (p: Patient) => {
    setUcSelected(p);
    setUcResults([]);
    setUcName(getClientName(p) || p.patient_name);
    if (!ucStartDate) setUcStartDate(addDays(selectedDate, 1));

    let types: string[] = [];
    try {
      const pref = (await axios.get(`${API}/api/patients/${p.id}/shift-preferences`, { headers: h })).data;
      types = (pref?.shift_order || []).map((s: any) => s?.type).filter(Boolean);
    } catch { /* fall through to duty-type default */ }
    if (types.length === 0) types = shiftTypesByPatient[p.id] || [];
    if (types.length === 0) {
      const dutyDefs = DUTY_SHIFTS[getDutyLabel(p)] || (p.duty_type ? DUTY_SHIFTS[p.duty_type] : undefined) || [];
      types = dutyDefs.map(d => d.type);
    }
    setUcShifts(Array.from(new Set(types.filter(t => SHIFT_OPTIONS.some(o => o.type === t)))));
  };

  const toggleUcShift = (type: string) =>
    setUcShifts(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);

  // Create the upcoming case (Option A): set shift columns + start date, then put it
  // on that day's board so it's ready to assign.
  const ucCreate = async () => {
    if (!ucSelected) return;
    if (!ucStartDate) { window.alert('Pick a start date.'); return; }
    if (ucShifts.length === 0) { window.alert('Select at least one shift.'); return; }
    setUcSaving(true);
    const p = ucSelected;
    let step = '';
    try {
      // 1) Shift columns (persisted shift_order → the case's shift rows).
      step = 'saving shifts';
      const shift_order = ucShifts.map(type => {
        const opt = SHIFT_OPTIONS.find(o => o.type === type);
        return { id: type.toLowerCase().replace(/\s+/g, '_'), label: opt?.label || type, type };
      });
      await axios.post(`${API}/api/patients/${p.id}/shift-preferences`, { shift_order }, { headers: h });

      // 2) Start date (makes it show under Upcoming Cases).
      step = 'setting start date';
      const current = (await axios.get(`${API}/api/patients/${p.id}`, { headers: h })).data;
      const fd = new FormData();
      fd.append('patient_data', JSON.stringify({
        ...current, case_start_date: ucStartDate,
        clients_info: (current.patient_clients || []).map((c: any) => ({
          id: c.id, name: c.client_name, relationship: c.relationship,
          contact_number: c.contact_number_1, secondary_contact_number: c.contact_number_2, email: c.email,
        })),
      }));
      await axios.patch(`${API}/api/patients/${p.id}`, fd, { headers: { ...h, 'Content-Type': 'multipart/form-data' } });

      // 3) Put it on the start-date board (ready to assign).
      step = 'adding to board';
      await axios.post(`${API}/api/ops-board/override`, { patient_id: p.id, date: ucStartDate, on_board: true }, { headers: h });

      await fetchAll();
      await fetchUpcomingAsgn();
      ucReset();
    } catch (e: any) {
      console.error('create upcoming case failed at:', step, e?.response?.status, e?.response?.data);
      const detail = e?.response?.data?.detail ?? e?.message ?? 'unknown error';
      window.alert(`Failed while ${step} (HTTP ${e?.response?.status ?? '?'}):\n${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
    } finally {
      setUcSaving(false);
    }
  };

  // ── Assign handlers ──────────────────────────────────────────────────────────

  const handleAssignClick = (patient: Patient) => {
    setSelectedPatient(patient);
    setIsAssignModalOpen(true);
  };

  // Opens modal with a specific shift pre-selected (nurse pill or empty slot click)
  const openAssignModal = (patient: Patient, shiftLabel: string, session: string, carriedNurseId?: string, date?: string) => {
    setSelectedPatient(patient);
    setInitialSlots([{ date: date || activeDate, shiftId: shiftLabel, session }]);
    setPreNurseId(carriedNurseId);
    setIsAssignModalOpen(true);
  };

  // Open the assignment grid for a case with nothing pre-selected, so a brand-new
  // shift can be added (pick day + shift in the grid).
  const openAddShift = (patient: Patient) => {
    setSelectedPatient(patient);
    setInitialSlots([]);
    setPreNurseId(undefined);
    setIsAssignModalOpen(true);
  };

  // Opens the "which patient + shift?" picker for Available Today flow
  const openAvailAssign = (nurse: StaffMember) => {
    setAvailNurse({ id: nurse.id, name: (nurse as any).full_name || '' });
    setAvailPatientId('');
    setAvailShift(null);
    setAvailClientSearch('');
    setAvailModal(true);
  };

  // Confirms Available Today assignment — opens AssignNurseModal with slot + nurse pre-filled
  const confirmAvailAssign = () => {
    if (!availPatientId || !availShift || !availNurse) return;
    const patient = patients.find(p => String(p.id) === availPatientId);
    if (!patient) return;
    setAvailModal(false);
    setSelectedPatient(patient);
    const aType = (availShift as any).type || availShift.session;
    setInitialSlots([{ date: activeDate, shiftId: aType.toLowerCase().replace(/\s+/g, '_'), session: aType }]);
    setPreNurseId(String(availNurse.id));
    setIsAssignModalOpen(true);
  };

  // ── Availability handlers ─────────────────────────────────────────────────────

  // Copy a patient's TODAY staffing onto TOMORROW (drag a Today card up into the
  // Tomorrow section). Persists to schedule_shifts via /schedule/carry-day, then
  // refetches the tomorrow assignments so the Tomorrow board reflects it.
  const carryToTomorrow = async (patientId: number) => {
    const fromDate = selectedDate;             // "today" anchor
    const toDate = addDays(selectedDate, 1);   // tomorrow
    try {
      await axios.post(`${API}/api/schedule/carry-day`,
        { patient_id: patientId, from_date: fromDate, to_date: toDate }, { headers: h });
      // Make sure the case is ON tomorrow's board so the carried card actually shows
      // (it may have been removed from tomorrow earlier).
      await axios.post(`${API}/api/ops-board/override`,
        { patient_id: patientId, date: toDate, on_board: true }, { headers: h });
      setBoardOverrides(prev => ({ ...prev, [`${patientId}|${toDate}`]: true }));
      const aRes = await axios.get(`${API}/api/assignments`, { headers: h, params: { start_date: toDate, end_date: toDate } });
      setTomorrowAsgn(aRes.data || []);
      setCarriedIds(prev => new Set(prev).add(patientId));
    } catch (e) { console.error('carry to tomorrow', e); }
  };

  // Change a nurse's availability category for the active day.
  const setAvailability = async (nurseId: number, category: string) => {
    // Guard: warn before overwriting a status she's PERMANENTLY under (Resigned /
    // Absconded / Uncertainty) — same warning pattern as assigning an on-leave
    // nurse. Declining leaves her exactly as she was; nothing is written.
    const previous = nurseAvail.find(r => r.nurse_id === nurseId)?.category;
    if (previous && previous !== category && PERMANENT_AVAIL_STATUSES.includes(previous)) {
      const ok = window.confirm(`This staff is currently marked ${previous}.\n\nDo you want to change their status to "${category}"?`);
      if (!ok) return;
    }

    setNurseAvail(prev => prev.map(r => r.nurse_id === nurseId ? { ...r, category } : r)); // optimistic
    try {
      await axios.post(`${API}/api/nurse-availability`, { nurse_id: nurseId, date: availDate, category }, { headers: h });
      fetchLeavePeriods();   // keep the Leave Tracker in sync (it reads a separate feed)
    } catch (e) { console.error('set availability', e); fetchAvailability(); }
  };

  // When an unavailable nurse is assigned anyway (confirmed in AssignNurseModal),
  // flip each of them to Ready Available for the active day.
  // Called when a nurse marked on-leave is assigned anyway (OK on the warning).
  // She's back as of availDate → TRIM her leave to end the day before, so the
  // Leave Tracker (which reads the from/to stamp) actually shrinks instead of
  // leaving a stale full-range card. If availDate is the first leave day the
  // whole period is cleared; otherwise the head [from … availDate-1] is kept.
  const overrideAvailabilityToAvailable = async (nurseIds: number[]) => {
    setNurseAvail(prev => prev.map(r => nurseIds.includes(r.nurse_id) ? { ...r, category: DEFAULT_AVAIL } : r)); // optimistic
    const dayBefore = addDays(availDate, -1);
    try {
      await Promise.all(nurseIds.map(async (id) => {
        const row = nurseAvail.find(r => r.nurse_id === id);
        const cat = row?.category;
        if (cat && PERMANENT_AVAIL_STATUSES.includes(cat)) {
          // Permanent status (Resigned/Absconded/Uncertainty) — always an
          // open-ended row (no to_date), so the leave-range trim below can't
          // touch it (from&&to would be false, silently no-op-ing). The plain
          // POST already knows how to correctly close an open permanent period
          // — same endpoint the confirm-guard callers use.
          await axios.post(`${API}/api/nurse-availability`, { nurse_id: id, date: availDate, category: DEFAULT_AVAIL }, { headers: h });
          return;
        }
        const from = row?.from_date ? String(row.from_date).split('T')[0] : null;
        const to = row?.to_date ? String(row.to_date).split('T')[0] : null;
        if (from && to) {
          // wipe the whole leave period, then restore only the head before she's back
          await axios.delete(`${API}/api/nurse-availability/leave`, { headers: h, params: { nurse_id: id, from_date: from, to_date: to } });
          if (from <= dayBefore) {
            await axios.post(`${API}/api/nurse-availability/leave`, { nurse_id: id, from_date: from, to_date: dayBefore, category: cat }, { headers: h });
          }
        } else {
          // no leave-period stamp — just clear this single day's override
          await axios.delete(`${API}/api/nurse-availability`, { headers: h, params: { nurse_id: id, date: availDate } });
        }
      }));
    } catch (e) { console.error('override availability (trim leave)', e); }
    fetchAvailability();
    fetchLeavePeriods();
  };

  // nurse_id -> blocked status for the active day (drives the assign-time warning).
  const blockedStaffMap: Record<number, string> = {};
  nurseAvail.forEach(r => { if (r.nurse_id != null && BLOCKED_AVAIL_STATUSES.includes(r.category)) blockedStaffMap[r.nurse_id] = r.category; });

  // Move a nurse's availability to a different date (re-buckets them off this day).
  const moveAvailabilityDate = async (nurseId: number, category: string, newDate: string) => {
    if (newDate === availDate) return;
    try {
      await axios.post(`${API}/api/nurse-availability`, { nurse_id: nurseId, date: newDate, category }, { headers: h });
      await axios.delete(`${API}/api/nurse-availability`, { headers: h, params: { nurse_id: nurseId, date: availDate } });
      fetchAvailability();
      fetchLeavePeriods();   // keep the Leave Tracker in sync
    } catch (e) { console.error('move availability', e); fetchAvailability(); }
  };

  // Add a new availability category option (reference: ops_availability).
  const addAvailOption = async (label: string) => {
    const clean = label.trim();
    if (!clean || availOptions.includes(clean)) return;
    const key = 'ops_' + clean.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    try {
      await axios.post(`${API}/api/reference/add`, { category: 'ops_availability', key, label: clean, is_active: true }, { headers: h });
      setNewOptDraft('');
      loadAvailOptions();
    } catch (e) { console.error('add option', e); }
  };

  // ── Leave tracker handlers (leave = a leave-type availability category) ──────
  // Add OR edit a leave period. When editing and the range changed, the old days are
  // cleared first so the period truly moves rather than leaving stragglers behind.
  const addLeave = async () => {
    if (!leaveForm.nurse_id) { alert('Select a staff member'); return; }
    const from = leaveForm.from_date || availDate;
    const to = leaveForm.to_date || from;
    try {
      if (leaveEdit && (leaveEdit.from_date !== from || leaveEdit.to_date !== to)) {
        await axios.delete(`${API}/api/nurse-availability/leave`, { headers: h, params: { nurse_id: leaveEdit.nurse_id, from_date: leaveEdit.from_date, to_date: leaveEdit.to_date } });
      }
      await axios.post(`${API}/api/nurse-availability/leave`,
        { nurse_id: Number(leaveForm.nurse_id), from_date: from, to_date: to, category: leaveForm.category }, { headers: h });
      setLeaveModal(false);
      setLeaveEdit(null);
      fetchAvailability();
      fetchLeavePeriods();
    } catch (e) { console.error('add leave', e); alert('Failed to add leave'); }
  };

  // Open the leave modal pre-filled to edit an existing period (range + type).
  const openEditLeave = (card: any) => {
    const from = card.from_date || availDate;
    const to = card.to_date || from;
    setLeaveForm({ nurse_id: String(card.nurse_id), category: card.category, from_date: from, to_date: to });
    setLeaveEdit({ nurse_id: card.nurse_id, from_date: from, to_date: to });
    setLeaveModal(true);
  };

  // Remove a leave period → delete every day in its range (reverts to Ready Available).
  const removeFromLeave = async (card: any) => {
    const from = card.from_date || availDate;
    const to = card.to_date || from;
    try {
      await axios.delete(`${API}/api/nurse-availability/leave`, { headers: h, params: { nurse_id: card.nurse_id, from_date: from, to_date: to } });
      fetchAvailability();
      fetchLeavePeriods();
    } catch (e) { console.error('remove leave', e); fetchAvailability(); }
  };

  // Drop a dragged leave card into another column → re-stamp the whole period to that type.
  const dropLeave = async (category: string) => {
    const card = dragCard;
    setDragCard(null);
    setHoverCol(null);
    if (!card || card.category === category) return;
    const from = card.from_date || availDate;
    const to = card.to_date || from;
    try {
      await axios.post(`${API}/api/nurse-availability/leave`,
        { nurse_id: card.nurse_id, from_date: from, to_date: to, category }, { headers: h });
      fetchAvailability();
      fetchLeavePeriods();
    } catch (e) { console.error('move leave', e); fetchAvailability(); }
  };

  // ── Patient criticality (persisted) ──────────────────────────────────────────

  const setCriticality = async (patient: Patient, level: string) => {
    setStatusMenuId(null);
    // optimistic local update (cards render from allPatients; stats from patients)
    setAllPatients(prev => prev.map(x => x.id === patient.id ? ({ ...x, criticality: level } as Patient) : x));
    setPatients(prev => prev.map(x => x.id === patient.id ? ({ ...x, criticality: level } as Patient) : x));
    try {
      const current = (await axios.get(`${API}/api/patients/${patient.id}`, { headers: h })).data;
      const fd = new FormData();
      fd.append('patient_data', JSON.stringify({
        ...current, criticality: level,
        clients_info: (current.patient_clients || []).map((c: any) => ({
          id: c.id, name: c.client_name, relationship: c.relationship,
          contact_number: c.contact_number_1, secondary_contact_number: c.contact_number_2, email: c.email,
        })),
      }));
      await axios.patch(`${API}/api/patients/${patient.id}`, fd, { headers: { ...h, 'Content-Type': 'multipart/form-data' } });
    } catch (e) {
      console.error('Failed to set criticality', e);
    }
  };

  // ── Patient VIP flag (persisted) ─────────────────────────────────────────────
  const setPatientVip = async (patient: Patient, is_vip: boolean) => {
    // optimistic
    setAllPatients(prev => prev.map(x => x.id === patient.id ? ({ ...x, is_vip } as Patient) : x));
    setPatients(prev => prev.map(x => x.id === patient.id ? ({ ...x, is_vip } as Patient) : x));
    try {
      const current = (await axios.get(`${API}/api/patients/${patient.id}`, { headers: h })).data;
      const fd = new FormData();
      fd.append('patient_data', JSON.stringify({
        ...current, is_vip,
        clients_info: (current.patient_clients || []).map((c: any) => ({
          id: c.id, name: c.client_name, relationship: c.relationship,
          contact_number: c.contact_number_1, secondary_contact_number: c.contact_number_2, email: c.email,
        })),
      }));
      await axios.patch(`${API}/api/patients/${patient.id}`, fd, { headers: { ...h, 'Content-Type': 'multipart/form-data' } });
    } catch (e) {
      console.error('Failed to set VIP', e);
    }
  };

  // ── Per-day case note (persisted per patient+date in ops_board_notes) ─────────
  const openNote = (patient: Patient, date: string) => {
    setNotePatient(patient);
    setNoteDate(date);
    const existing = boardNotes[`${patient.id}|${date}`];
    setNoteDraft(existing?.note || '');
    setNoteColorDraft(existing?.color || 'black');
  };

  const saveNote = async () => {
    if (!notePatient || !noteDate) return;
    const note = noteDraft.trim();
    const color = noteColorDraft;
    const key = `${notePatient.id}|${noteDate}`;
    setNoteSaving(true);
    try {
      await axios.post(`${API}/api/ops-board/note`,
        { patient_id: notePatient.id, date: noteDate, note, color }, { headers: h });
      // Update only THIS day's note — other days are untouched.
      setBoardNotes(prev => ({ ...prev, [key]: { note, color } }));
      setNotePatient(null);
    } catch (e) {
      console.error('Failed to save note', e);
      alert('Failed to save note.');
    } finally {
      setNoteSaving(false);
    }
  };

  // Effective board membership for a given day: a per-day override wins, else the
  // baseline on_ops_board. So Yesterday / Today / Tomorrow each curate their own set.
  // The board is a per-day CHANGE list, not a roster: a case shows on a day ONLY
  // if a change was explicitly logged for that exact date (an ops-board override
  // set to true). No baseline on_ops_board fallback — that used to auto-show a
  // case on every day and made changes bleed forward.
  const isOnBoardForDay = (p: Patient, date: string) => boardOverrides[`${p.id}|${date}`] === true;
  const activeDayPatients = allPatients.filter(p => isOnBoardForDay(p, activeDate));

  // The board set for a given day — sorted (duty then criticality) + search-filtered.
  const visibleForDate = (date: string) => {
    const sorted = allPatients.filter(p => isOnBoardForDay(p, date)).sort((a, b) => {
      const dr = dutyRank(a) - dutyRank(b);
      if (dr !== 0) return dr;
      const sa = CRITICALITY_ORDER[normStatus((a as any).criticality)] ?? 0;
      const sb = CRITICALITY_ORDER[normStatus((b as any).criticality)] ?? 0;
      return sa - sb;
    });
    const q = boardSearch.trim().toLowerCase();
    return q
      ? sorted.filter(p => (p.patient_name || '').toLowerCase().includes(q)
          || getClientName(p).toLowerCase().includes(q)
          || `lhhs${p.id}`.includes(q))
      : sorted;
  };

  const visiblePatients = visibleForDate(activeDate);
  // The Tomorrow view's bottom "Today" drag-source strip is an EXACT mirror of the
  // real Today board (selectedDate) — same cases, so it always matches the Today tab.
  const todayStripPatients = visibleForDate(selectedDate);
  // The Today view's "Yesterday" reference strip is an EXACT mirror of the real
  // Yesterday board (selectedDate − 1).
  const yesterdayBoardPatients = visibleForDate(addDays(selectedDate, -1));

  // ─── Styles ──────────────────────────────────────────────────────────────────

  const css = {
    page:        { background: '#f8fafc', minHeight: '100vh', fontFamily: '"Inter",-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif', fontSize: '13px', color: '#0f172a' } as React.CSSProperties,
    statsBar:    { display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '10px', padding: '12px 20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' } as React.CSSProperties,
    statCard:    { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' } as React.CSSProperties,
    statIcon:    { width: '36px', height: '36px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } as React.CSSProperties,
    statVal:     { fontSize: '20px', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em' } as React.CSSProperties,
    statLbl:     { fontSize: '9px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '3px' } as React.CSSProperties,
    boardWrap:   { display: 'grid', gridTemplateColumns: '1fr 272px', gap: '12px', padding: '12px 20px', alignItems: 'start' } as React.CSSProperties,
    casesGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: '10px' } as React.CSSProperties,
    panel:       { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' } as React.CSSProperties,
    panelHead:   { padding: '10px 14px', borderBottom: '1px solid #e2e8f0', background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'space-between' } as React.CSSProperties,
    panelTitle:  { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569' } as React.CSSProperties,
    cnt:         { fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', color: '#fff' } as React.CSSProperties,
  };

  // ─── Render helpers ───────────────────────────────────────────────────────────

  const dutyPillStyle = (code: string): React.CSSProperties => {
    const map: Record<string, React.CSSProperties> = {
      '08': { background: '#eff6ff', color: '#1d4ed8', border: '1.5px solid #bfdbfe' },
      '12': { background: '#f5f3ff', color: '#6d28d9', border: '1.5px solid #ddd6fe' },
      '24': { background: '#f0fdfa', color: '#0f766e', border: '1.5px solid #99f6e4' },
    };
    return { display: 'inline-flex', alignItems: 'center', padding: '2px 7px', borderRadius: '999px', fontSize: '10px', fontWeight: 700, letterSpacing: '0.02em', ...map[code] || map['08'] };
  };

  const nursePillStyle = (hasAssignment: boolean): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', padding: '2px 8px',
    borderRadius: '999px', fontSize: '10px', fontWeight: 600,
    background: hasAssignment ? '#f1f5f9' : '#fef2f2',
    color: hasAssignment ? '#334155' : '#dc2626',
    border: hasAssignment ? '1.5px solid #e2e8f0' : '1.5px dashed #fca5a5',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  });

  const renderCaseCard = (patient: Patient, serial?: number) => {
    const shifts = getShiftsForPatient(patient, activeAssignments);
    const dutyCode = getDutyShortCode(getDutyLabel(patient));
    const bStatus = normStatus((patient as any).criticality);
    const bCfg = CRITICALITY_CFG[bStatus] || CRITICALITY_CFG[DEFAULT_CRITICALITY];
    const clientName = getClientName(patient);
    const isVip = !!(patient as any).is_vip;

    const leftBorderColor = dutyCode === '12' ? '#7c3aed' : dutyCode === '24' ? '#0d9488' : '#3b82f6';

    return (
      <div key={patient.id} style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px',
        overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        borderLeft: `4px solid ${leftBorderColor}`, position: 'relative',
      }}>
        {/* Card header — tinted by patient criticality */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', background: bCfg.bg, borderBottom: `1px solid ${bCfg.border}` }}>
          {serial != null && (
            <span style={{ flexShrink: 0, width: '22px', height: '22px', borderRadius: '50%', background: '#0f172a', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, letterSpacing: '-0.02em' }}>{serial}</span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
              <div style={{ fontWeight: isVip ? 800 : 700, fontSize: '12.5px', color: isVip ? VIP_GOLD : '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {isVip && <span style={{ marginRight: '3px' }}>★</span>}{clientName || patient.patient_name}
              </div>
            </div>
            <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {clientName ? patient.patient_name : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0 }}>
            {/* VIP — compact star toggle (highlights the client name in gold) */}
            <button
              onClick={isEditable ? (e) => { e.stopPropagation(); setPatientVip(patient, !isVip); } : undefined}
              title={!isEditable ? (isVip ? 'VIP client' : undefined) : (isVip ? 'VIP client — click to unmark' : 'Mark as VIP client')}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', borderRadius: '50%', border: `1.5px solid ${isVip ? '#fcd34d' : '#e2e8f0'}`, background: isVip ? '#fffbeb' : '#fff', color: isVip ? VIP_GOLD : '#cbd5e1', cursor: isEditable ? 'pointer' : 'default', fontSize: '12px', lineHeight: 1, padding: 0, flexShrink: 0 }}>
              ★
            </button>
            <span style={dutyPillStyle(dutyCode)}>{dutyCode}hr</span>
            {/* Criticality badge */}
            <span
              title="Case status"
              onClick={isEditable ? (e) => {
                e.stopPropagation();
                if (statusMenuId === patient.id) { setStatusMenuId(null); return; }
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const left = Math.min(r.right - 150, window.innerWidth - 158);
                setMenuPos({ top: r.bottom + 6, left: Math.max(8, left) });
                setStatusMenuId(patient.id);
              } : undefined}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 7px', borderRadius: '999px', fontSize: '9px', fontWeight: 700, background: bCfg.bg, color: bCfg.color, border: `1.5px solid ${bCfg.border}`, cursor: isEditable ? 'pointer' : 'default', letterSpacing: '0.03em', position: 'relative' }}
            >
              <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: bCfg.dot, flexShrink: 0 }} />
              {bStatus}
            </span>
            {/* Remove card from board — editable days only (yesterday is read-only) */}
            {isEditable && (
              <button
                onClick={(e) => { e.stopPropagation(); removePatientFromBoard(patient); }}
                title="Remove from board"
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '18px', height: '18px', borderRadius: '50%', border: '1px solid #fecaca', background: '#fff1f2', color: '#dc2626', cursor: 'pointer', fontSize: '12px', lineHeight: 1, padding: 0, flexShrink: 0 }}
                onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff1f2'; }}
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Criticality dropdown — portal + fixed position so the card's overflow can't clip it */}
        {statusMenuId === patient.id && menuPos && createPortal(
          <div ref={statusMenuRef} style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.14)', zIndex: 9999, minWidth: '150px', padding: '4px' }}>
            <div style={{ fontSize: '9px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '4px 12px 2px' }}>Status</div>
            {CRITICALITY_OPTIONS.map(opt => {
              const cfg = CRITICALITY_CFG[opt];
              const isSel = bStatus === opt;
              return (
                <div key={opt} onClick={() => setCriticality(patient, opt)} style={{ padding: '7px 12px', fontSize: '11px', fontWeight: isSel ? 700 : 600, color: isSel ? cfg.color : '#334155', background: isSel ? cfg.bg : 'transparent', cursor: 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}
                  onMouseEnter={e => { if (!isSel) (e.currentTarget.style.background = '#f8fafc'); }}
                  onMouseLeave={e => { if (!isSel) (e.currentTarget.style.background = 'transparent'); }}
                >
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
                  {opt}
                </div>
              );
            })}
          </div>,
          document.body
        )}

        {/* Shift rows */}
        {shifts.map((shift, idx) => {
          const fk = flagKey(patient.id, activeDate, shift.session);
          const isManual = manualFlags.has(fk);
          const flagged = isManual || autoChanges.has(fk);  // yellow when EITHER marker is set
          return (
          <div key={`${shift.shiftId}-${idx}`} style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', borderBottom: '1px solid #f8fafc', gap: '8px', background: flagged ? '#fef9c3' : undefined }}>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: '58px' }}>
              {shift.label}
            </span>
            <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '3px', alignItems: 'center' }}>
              {shift.names.length > 0
                ? shift.names.map(name => (
                    <span key={name}
                      style={{ ...nursePillStyle(true), cursor: isEditable ? 'pointer' : 'default' }}
                      onClick={isEditable ? () => openAssignModal(patient, shift.shiftId, shift.session) : undefined}>
                      {name}
                    </span>
                  ))
                : isEditable
                  ? (
                    <span style={nursePillStyle(false)} onClick={() => openAssignModal(patient, shift.shiftId, shift.session)}>
                      + Assign
                    </span>
                  )
                  : (
                    <span style={{ fontSize: '10px', fontWeight: 600, color: '#cbd5e1', fontStyle: 'italic' }}>—</span>
                  )
              }
            </div>
            {/* Manual 🚩 — team pre-warning that a change may happen here (card only) */}
            {isEditable && (
              <button onClick={(e) => { e.stopPropagation(); toggleShiftFlag(patient, shift.session); }}
                title={isManual ? 'Flag on — click to clear' : 'Flag: a change may happen here soon'}
                style={{ flexShrink: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: '2px', borderRadius: '5px', opacity: isManual ? 1 : 0.4, filter: isManual ? 'none' : 'grayscale(1)' }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.filter = 'none'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = isManual ? '1' : '0.4'; e.currentTarget.style.filter = isManual ? 'none' : 'grayscale(1)'; }}>
                🚩
              </button>
            )}
          </div>
        );})}

        {/* Add a new shift to this case */}
        {isEditable && (
          <div style={{ padding: '6px 12px 0' }}>
            <button onClick={() => openAddShift(patient)}
              style={{ width: '100%', border: '1px dashed #cbd5e1', background: '#fff', color: '#64748b', borderRadius: '7px', padding: '5px', fontSize: '10.5px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#2563eb'; e.currentTarget.style.background = '#eff6ff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = '#fff'; }}>
              <span style={{ fontSize: '13px', lineHeight: 1 }}>+</span> Add shift
            </button>
          </div>
        )}

        {/* Note view-box — per-day note for THIS board's active date */}
        {(() => {
          const dayNote = boardNotes[`${patient.id}|${activeDate}`];
          const note = dayNote?.note ? dayNote.note.trim() : '';
          const col = NOTE_COLORS[dayNote?.color || 'black'] || NOTE_COLORS.black;
          if (!note && !isEditable) return null;   // nothing to show on read-only days
          return (
            <div style={{ padding: '6px 12px' }}>
              <div onClick={() => isEditable && openNote(patient, activeDate)}
                title={isEditable ? (note ? 'Click to edit note' : 'Click to add a note') : undefined}
                style={{
                  cursor: isEditable ? 'pointer' : 'default', borderRadius: '7px', padding: '7px 10px',
                  fontSize: '11px', lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  border: `1px ${note ? 'solid' : 'dashed'} ${note ? col : '#cbd5e1'}`,
                  background: note ? `${col}0f` : '#fff',
                  color: note ? col : '#94a3b8', fontWeight: note ? 600 : 700,
                }}>
                {note || '＋ Add note'}
              </div>
            </div>
          );
        })()}

      </div>
    );
  };

  // ─── Today source card (Tomorrow board only) — draggable into the Tomorrow section ──
  const renderTodaySourceCard = (patient: Patient, serial?: number) => {
    const shifts = getShiftsForPatient(patient, assignments); // TODAY's staffing
    const dutyCode = getDutyShortCode(getDutyLabel(patient));
    const clientName = getClientName(patient);
    const carried = carriedIds.has(patient.id);
    const dragging = dragTodayId === patient.id;
    // Mirror the real case card so Today (bottom) and Tomorrow (top) are comparable.
    const bStatus = (patient as any).criticality || DEFAULT_CRITICALITY;
    const bCfg = CRITICALITY_CFG[bStatus] || CRITICALITY_CFG[DEFAULT_CRITICALITY];
    const leftBorderColor = dutyCode === '12' ? '#7c3aed' : dutyCode === '24' ? '#0d9488' : '#3b82f6';
    return (
      <div key={`today-${patient.id}`}
        draggable
        onDragStart={(e) => {
          setDragTodayId(patient.id);
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', String(patient.id));  // required for native DnD to start
        }}
        onDragEnd={() => { setDragTodayId(null); setTmrwOver(false); }}
        title="Drag up into Tomorrow to copy today's staffing"
        style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden',
          boxShadow: dragging ? '0 10px 24px -8px rgba(15,23,42,0.3)' : '0 1px 3px rgba(0,0,0,0.04)',
          borderLeft: `4px solid ${leftBorderColor}`,
          opacity: dragging ? 0.6 : 1, cursor: 'grab', transition: 'box-shadow .15s ease, opacity .15s ease',
        }}>
        {/* Header — mirrors the case card (criticality-tinted), read-only for comparison */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', background: bCfg.bg, borderBottom: `1px solid ${bCfg.border}` }}>
          <span style={{ color: '#cbd5e1', fontSize: '13px', lineHeight: 1, flexShrink: 0 }}>⠿</span>
          {serial != null && (
            <span style={{ flexShrink: 0, width: '22px', height: '22px', borderRadius: '50%', background: '#0f172a', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, letterSpacing: '-0.02em' }}>{serial}</span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '12.5px', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{clientName || patient.patient_name}</div>
            <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{clientName ? patient.patient_name : ''}</div>
          </div>
          <span style={dutyPillStyle(dutyCode)}>{dutyCode}hr</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 7px', borderRadius: '999px', fontSize: '9px', fontWeight: 700, background: bCfg.bg, color: bCfg.color, border: `1.5px solid ${bCfg.border}`, letterSpacing: '0.03em' }}>
            <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: bCfg.dot, flexShrink: 0 }} />{bStatus}
          </span>
          {carried && <span style={{ fontSize: '9px', fontWeight: 700, color: '#15803d', background: '#dcfce7', borderRadius: '999px', padding: '2px 7px', whiteSpace: 'nowrap' }}>✓ Sent</span>}
          {/* Dismiss this suggestion (no changes to carry over) — hides it from the Today strip only */}
          <button
            onClick={(e) => { e.stopPropagation(); setHiddenTodaySrcIds(prev => { const n = new Set(prev); n.add(patient.id); return n; }); }}
            onMouseDown={(e) => e.stopPropagation()}
            title="Hide this suggestion (no changes to carry over)"
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '18px', height: '18px', borderRadius: '50%', border: '1px solid #e2e8f0', background: '#fff', color: '#94a3b8', cursor: 'pointer', fontSize: '12px', lineHeight: 1, padding: 0, flexShrink: 0 }}
            onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fecaca'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.borderColor = '#e2e8f0'; }}>
            ×
          </button>
        </div>
        {/* Shift rows — mirror the case card (read-only) */}
        {shifts.map((shift, idx) => (
          <div key={`${shift.shiftId}-${idx}`} style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', borderBottom: '1px solid #f8fafc', gap: '8px' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: '58px' }}>{shift.label}</span>
            <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '3px', alignItems: 'center' }}>
              {shift.names.length > 0
                ? shift.names.map(name => <span key={name} style={nursePillStyle(true)}>{name}</span>)
                : <span style={{ fontSize: '10px', fontWeight: 600, color: '#cbd5e1', fontStyle: 'italic' }}>—</span>}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ─── Read-only reference card for a PAST day (Today view's Yesterday strip) ──
  const renderRefCard = (patient: Patient, list: Assignment[], serial?: number, onHide?: () => void) => {
    const shifts = getShiftsForPatient(patient, list);
    const dutyCode = getDutyShortCode(getDutyLabel(patient));
    const clientName = getClientName(patient);
    const bStatus = (patient as any).criticality || DEFAULT_CRITICALITY;
    const bCfg = CRITICALITY_CFG[bStatus] || CRITICALITY_CFG[DEFAULT_CRITICALITY];
    const leftBorderColor = dutyCode === '12' ? '#7c3aed' : dutyCode === '24' ? '#0d9488' : '#3b82f6';
    return (
      <div key={`ref-${patient.id}`} style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)', borderLeft: `4px solid ${leftBorderColor}`, opacity: 0.92,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', background: bCfg.bg, borderBottom: `1px solid ${bCfg.border}` }}>
          {serial != null && (
            <span style={{ flexShrink: 0, width: '22px', height: '22px', borderRadius: '50%', background: '#0f172a', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, letterSpacing: '-0.02em' }}>{serial}</span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '12.5px', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{clientName || patient.patient_name}</div>
            <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{clientName ? patient.patient_name : ''}</div>
          </div>
          <span style={dutyPillStyle(dutyCode)}>{dutyCode}hr</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 7px', borderRadius: '999px', fontSize: '9px', fontWeight: 700, background: bCfg.bg, color: bCfg.color, border: `1.5px solid ${bCfg.border}`, letterSpacing: '0.03em' }}>
            <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: bCfg.dot, flexShrink: 0 }} />{bStatus}
          </span>
          {/* Hide this reference card (does NOT delete anything) */}
          {onHide && (
            <button
              onClick={(e) => { e.stopPropagation(); onHide(); }}
              title="Hide this card"
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '18px', height: '18px', borderRadius: '50%', border: '1px solid #e2e8f0', background: '#fff', color: '#94a3b8', cursor: 'pointer', fontSize: '12px', lineHeight: 1, padding: 0, flexShrink: 0 }}
              onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fecaca'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.borderColor = '#e2e8f0'; }}>
              ×
            </button>
          )}
        </div>
        {shifts.map((shift, idx) => (
          <div key={`${shift.shiftId}-${idx}`} style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', borderBottom: '1px solid #f8fafc', gap: '8px' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: '58px' }}>{shift.label}</span>
            <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '3px', alignItems: 'center' }}>
              {shift.names.length > 0
                ? shift.names.map(name => <span key={name} style={nursePillStyle(true)}>{name}</span>)
                : <span style={{ fontSize: '10px', fontWeight: 600, color: '#cbd5e1', fontStyle: 'italic' }}>—</span>}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ─── Add case card ─────────────────────────────────────────────────────────

  const renderAddCaseCard = () => (
    <div style={{ background: '#f8fafc', border: '2px dashed #cbd5e1', borderRadius: '12px', cursor: 'pointer', minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
      onClick={() => setAddingCase(true)}
      onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#3b82f6'; (e.currentTarget as HTMLDivElement).style.background = '#eff6ff'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#cbd5e1'; (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '20px' }}>
        <div style={{ width: '38px', height: '38px', borderRadius: '50%', border: '2px dashed #94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: 600, color: '#94a3b8' }}>+</div>
        <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>Add Case to Board</div>
        <div style={{ fontSize: '10px', color: '#94a3b8' }}>Search and select a patient</div>
      </div>
    </div>
  );

  // ─── Outsourcing section (red cards + add) ──────────────────────────────────
  const renderOutsourcingSection = () => (
    <div style={{ marginTop: '14px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <span style={{ fontSize: '12px', fontWeight: 800, color: '#be123c', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Outsourcing</span>
        <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>
          {activeOutsourcing.length} {activeOutsourcing.length === 1 ? 'entry' : 'entries'} · −₹{Math.round(mOutsourcing).toLocaleString('en-IN')} from sales
        </span>
      </div>
      <div style={css.casesGrid}>
        {activeOutsourcing.map(o => (
          <div key={o.id} style={{ background: '#fff5f5', border: '1px solid #fecaca', borderLeft: '4px solid #e11d48', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', background: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
              <span style={{ fontSize: '9px', fontWeight: 800, color: '#be123c', background: '#ffe4e6', border: '1px solid #fecdd3', borderRadius: '999px', padding: '2px 7px', letterSpacing: '0.03em', flexShrink: 0 }}>OUTSOURCED</span>
              <div style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: '12.5px', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.client_name}</div>
              {isEditable && (
                <button onClick={() => deleteOutsourcing(o.id)} title="Remove outsourcing entry"
                  style={{ width: '18px', height: '18px', borderRadius: '50%', border: '1px solid #fecaca', background: '#fff', color: '#dc2626', cursor: 'pointer', fontSize: '12px', lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
              )}
            </div>
            <div style={{ padding: '10px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Rate</span>
                <span style={{ fontSize: '16px', fontWeight: 800, color: '#e11d48' }}>−₹{Math.round(Number(o.rate) || 0).toLocaleString('en-IN')}</span>
              </div>
              {o.note && <div style={{ marginTop: '6px', fontSize: '11px', color: '#475569', lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{o.note}</div>}
            </div>
          </div>
        ))}
        {isEditable && (
          <div onClick={openOutsourcing}
            style={{ background: '#fff5f5', border: '2px dashed #fca5a5', borderRadius: '12px', cursor: 'pointer', minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#e11d48'; (e.currentTarget as HTMLDivElement).style.background = '#ffe4e6'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#fca5a5'; (e.currentTarget as HTMLDivElement).style.background = '#fff5f5'; }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '20px' }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '50%', border: '2px dashed #f87171', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: 600, color: '#e11d48' }}>+</div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#be123c' }}>Add Outsourcing</div>
              <div style={{ fontSize: '10px', color: '#fca5a5' }}>Client · note · rate</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ─── Add case search modal ──────────────────────────────────────────────────

  const renderAddCaseModal = () => (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}
      onClick={() => { setAddingCase(false); setAddSearch(''); setAllPatientsList([]); }}
    >
      <div style={{ background: '#fff', borderRadius: '14px', width: '440px', maxWidth: '92vw', maxHeight: '80vh', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Add Case to Board</div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>Search inactive or new patients to re-activate</div>
          </div>
          <button onClick={() => { setAddingCase(false); setAddSearch(''); setAllPatientsList([]); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '20px', lineHeight: 1, width: '28px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
        <input
          autoFocus
          value={addSearch}
          onChange={e => searchAllPatients(e.target.value)}
          placeholder="Search patient name or client…"
          style={{ width: '100%', padding: '11px 16px', border: 'none', borderBottom: '1px solid #e2e8f0', fontSize: '13px', outline: 'none', background: '#fafafa', fontFamily: 'inherit', color: '#0f172a' }}
        />
        <div style={{ flex: 1, overflowY: 'auto', maxHeight: '320px' }}>
          {allPatientsList.length === 0 && addSearch.length >= 2 && (
            <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>No patients found</div>
          )}
          {addSearch.length < 2 && (
            <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>Type at least 2 characters to search</div>
          )}
          {allPatientsList.map(p => {
            const client = getClientName(p);
            return (
              <div key={p.id} onClick={() => addPatientToBoard(p)}
                style={{ padding: '11px 16px', borderBottom: '1px solid #f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: '#0f172a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '13px', flexShrink: 0 }}>
                  {(client || p.patient_name)?.[0] || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client || p.patient_name}</div>
                  <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.patient_name}{p.duty_type ? ` · ${p.duty_type}` : ''}{p.status ? ` · ${p.status}` : ''}</div>
                </div>
                <span style={dutyPillStyle(getDutyShortCode(getDutyLabel(p)))}>{getDutyLabel(p)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const getNurseSkill = (n: any): string => {
    const c: string = n.clinical_class || '';
    if (c.includes('Class 1 P')) return 'Neonatal';
    if (c.includes('Class 1')) return 'ICU';
    if (c.includes('Class 2')) return 'ICU';
    if (c.includes('Class 3')) return 'General';
    if (c.includes('Class 4')) return 'Junior';
    return n.designation || 'General';
  };

  const getNurseRating = (n: any): string => {
    const map: Record<string, string> = {
      Outstanding: '5.0', 'High Profile': '4.5', 'Upper Medium': '4.0',
      Medium: '3.5', Average: '3.0', 'Low Profile': '2.5', 'Below Average': '2.0',
    };
    return map[n.grade_level] || '';
  };

  const getAssignedCase = (staffId: number): string => {
    // Match nurses AND physios (primary or assistant). Fall back to the scalar
    // *_id fields in case a nested staff object is missing.
    const a = assignments.find((x: any) =>
      x.nurse?.id === staffId || x.second_nurse?.id === staffId ||
      x.physio?.id === staffId || x.second_physio?.id === staffId ||
      x.nurse_id === staffId || x.second_nurse_id === staffId ||
      x.physio_id === staffId || x.second_physio_id === staffId
    );
    if (!a) return '—';
    const pid = a.patient_id ?? a.patient?.id;
    // Prefer the board patient; else the patient object carried on the assignment.
    const p = allPatients.find(x => x.id === pid) || patients.find(x => x.id === pid) || (a as any).patient;
    // Show the CLIENT name (fall back to patient name only if no client on file).
    return (p && getClientName(p)) || p?.patient_name || `Case #${pid}`;
  };

  // ─── Available Today panel ─────────────────────────────────────────────────

  const AVAIL_CAT_COLOR = (cat: string): string => {
    const m: Record<string, string> = {
      'Available': '#22c55e',
      'On Temporary Duty': '#2563eb',
      'Back-Up': '#f59e0b',
      'On Duty - Shuffle Possibility': '#4338ca',
      'On Duty': '#0284c7',
      'On Leave - Regular': '#d97706',
      'On Leave - Emergency': '#e11d48',
      'On Leave - Sick': '#dc2626',
      'Uncertainty': '#a16207',
      'Absconded': '#b91c1c',
      'Resigned': '#475569',
    };
    return m[cat] || '#94a3b8';
  };

  const renderAvailPanel = () => {
    // Nurses assigned to a case on the active day show as "On Duty" (derived from
    // assignments, overriding their stored availability category for display).
    // Use the active-tab's assignments (today / yesterday / tomorrow) so the panel's
    // "On Duty" matches the day the Yesterday/Today/Tomorrow tab is showing — not
    // always today's. (The Staff Pool tab deliberately keeps plain `assignments`,
    // since it has no day selector and follows the header calendar instead.)
    const onDutyIds = new Set(activeAssignments.flatMap(x => [x.nurse?.id, x.second_nurse?.id]).filter(Boolean));
    const availView = nurseAvail.map(r => (onDutyIds.has(r.nurse_id) ? { ...r, category: 'On Duty' } : r));

    // Group the day's availability rows by category, in the configured order
    // (then any extra categories present in the data — e.g. the derived "On Duty").
    const cats = [...availOptions];
    availView.forEach(r => { if (r.category && !cats.includes(r.category)) cats.push(r.category); });
    const q = availSearch.trim().toLowerCase();
    const grouped = cats
      .map(cat => ({ cat, rows: availView.filter(r => r.category === cat && (!q || (r.full_name || '').toLowerCase().includes(q))) }))
      .filter(g => g.rows.length > 0);

    const joiningToday = nurses.filter(n => (n as any).date_of_joining === availDate);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

        {/* Availability panel (per active day) — overflow visible so the section overlay can float */}
        <div style={{ ...css.panel, overflow: 'visible' }}>
          <div style={{ ...css.panelHead, borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
            <span style={css.panelTitle as React.CSSProperties}>Availability · {fmtDateShort(availDate)}</span>
            <span style={{ ...css.cnt, background: '#16a34a' }}>{nurseAvail.length}</span>
          </div>

          {/* Search availability list by staff name */}
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', position: 'relative' }}>
            <svg width="13" height="13" fill="none" stroke="#94a3b8" viewBox="0 0 24 24" style={{ position: 'absolute', left: '18px', top: '50%', transform: 'translateY(-50%)' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input value={availSearch} onChange={e => setAvailSearch(e.target.value)}
              placeholder="Search staff…"
              style={{ width: '100%', padding: '5px 24px 5px 26px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '11px', fontFamily: 'inherit', outline: 'none', color: '#0f172a', boxSizing: 'border-box' }} />
            {availSearch && (
              <button onClick={() => setAvailSearch('')} title="Clear" style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: '13px', lineHeight: 1 }}>×</button>
            )}
          </div>

          {(() => {
            const renderRow = (r: (typeof nurseAvail)[number]) => (
              <div key={r.nurse_id} style={{ padding: '8px 12px', borderBottom: '1px solid #f8fafc' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.full_name}</div>
                    <div style={{ fontSize: '10px', color: '#94a3b8' }}>{r.designation || 'Nurse'}{r.clinical_class ? ` · ${getNurseSkill(r)}` : ''}</div>
                  </div>
                  {isEditable && (
                    <button onClick={() => openAvailAssign({ id: r.nurse_id, full_name: r.full_name } as any)}
                      style={{ fontSize: '10px', fontWeight: 700, padding: '4px 9px', borderRadius: '6px', background: 'linear-gradient(135deg,#0ea5e9 0%,#2563eb 100%)', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' as const }}>
                      Assign
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
                  <select value={r.category} disabled={!isEditable}
                    onChange={e => setAvailability(r.nurse_id, e.target.value)}
                    style={{ flex: 1, minWidth: 0, padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: '5px', fontSize: '10px', fontFamily: 'inherit', background: isEditable ? '#fff' : '#f8fafc', color: '#334155', outline: 'none', cursor: isEditable ? 'pointer' : 'default' }}>
                    {availOptions.includes(r.category) ? null : <option value={r.category}>{r.category}</option>}
                    {availOptions.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <input type="date" value={availDate} disabled={!isEditable}
                    onChange={e => moveAvailabilityDate(r.nurse_id, r.category, e.target.value)}
                    title="Change the date to move this staff to another day"
                    style={{ padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: '5px', fontSize: '10px', fontFamily: 'inherit', background: isEditable ? '#fff' : '#f8fafc', color: '#334155', outline: 'none' }} />
                </div>
              </div>
            );

            if (grouped.length === 0)
              return <div style={{ padding: '20px', textAlign: 'center', fontSize: '11px', color: '#94a3b8' }}>No staff</div>;

            const availableGroup = grouped.find(g => g.cat === DEFAULT_AVAIL);
            const otherGroups = grouped.filter(g => g.cat !== DEFAULT_AVAIL);

            return (
              <div ref={availIndexRef} style={{ position: 'relative', maxHeight: '460px', overflowY: 'auto' }}>
                {/* Other statuses — a bar that hovers (sticky) over the Available list; click to pop staff */}
                {otherGroups.length > 0 && (
                  <div style={{ position: 'sticky', top: 0, zIndex: 5, background: '#fff', boxShadow: '0 4px 12px rgba(15,23,42,0.07)', borderBottom: '1px solid #e2e8f0' }}>
                    {otherGroups.map(group => {
                      const open = openAvailCat === group.cat;
                      return (
                        <div key={group.cat} style={{ position: 'relative' }}>
                          <div onClick={() => setOpenAvailCat(open ? null : group.cat)}
                            style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 12px', background: open ? '#eff6ff' : '#fff', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', userSelect: 'none', position: 'relative', zIndex: 1 }}
                            onMouseEnter={e => { if (!open) e.currentTarget.style.background = '#f8fafc'; }}
                            onMouseLeave={e => { if (!open) e.currentTarget.style.background = '#fff'; }}>
                            <span style={{ display: 'inline-flex', color: open ? '#2563eb' : '#94a3b8', transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform 0.15s', flexShrink: 0 }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" /></svg>
                            </span>
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: AVAIL_CAT_COLOR(group.cat), flexShrink: 0 }} />
                            <span style={{ fontSize: '11px', fontWeight: 800, color: open ? '#1d4ed8' : '#334155', textTransform: 'uppercase', letterSpacing: '0.03em', flex: 1 }}>{group.cat}</span>
                            <span style={{ fontSize: '10px', fontWeight: 800, color: '#fff', background: AVAIL_CAT_COLOR(group.cat), minWidth: '18px', textAlign: 'center', borderRadius: '999px', padding: '1px 6px' }}>{group.rows.length}</span>
                          </div>
                          {open && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 10px 10px', boxShadow: '0 14px 30px rgba(15,23,42,0.22)', maxHeight: '320px', overflowY: 'auto' }}>
                              {group.rows.map(renderRow)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Ready Available — open inline as the main list */}
                {availableGroup && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 12px', background: '#f0fdf4', borderBottom: '1px solid #f1f5f9' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: AVAIL_CAT_COLOR(DEFAULT_AVAIL), flexShrink: 0 }} />
                      <span style={{ fontSize: '11px', fontWeight: 800, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.03em', flex: 1 }}>{DEFAULT_AVAIL}</span>
                      <span style={{ fontSize: '10px', fontWeight: 800, color: '#fff', background: AVAIL_CAT_COLOR(DEFAULT_AVAIL), minWidth: '18px', textAlign: 'center', borderRadius: '999px', padding: '1px 6px' }}>{availableGroup.rows.length}</span>
                    </div>
                    {availableGroup.rows.map(renderRow)}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Add a new availability option — button that reveals an input */}
          {isEditable && (
            <div style={{ padding: '8px 10px', borderTop: '1px solid #f1f5f9' }}>
              {!showAddOpt ? (
                <button onClick={() => setShowAddOpt(true)}
                  style={{ width: '100%', fontSize: '11px', fontWeight: 700, padding: '6px 10px', borderRadius: '6px', border: '1px dashed #cbd5e1', cursor: 'pointer', background: '#fff', color: '#64748b', fontFamily: 'inherit' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#2563eb'; e.currentTarget.style.background = '#eff6ff'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = '#fff'; }}>
                  + Add availability option
                </button>
              ) : (
                <div style={{ display: 'flex', gap: '5px' }}>
                  <input autoFocus value={newOptDraft} onChange={e => setNewOptDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { addAvailOption(newOptDraft); setShowAddOpt(false); } if (e.key === 'Escape') { setNewOptDraft(''); setShowAddOpt(false); } }}
                    placeholder="New option name…"
                    style={{ flex: 1, minWidth: 0, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '11px', fontFamily: 'inherit', outline: 'none', color: '#0f172a' }} />
                  <button onClick={() => { addAvailOption(newOptDraft); setShowAddOpt(false); }} disabled={!newOptDraft.trim()}
                    style={{ fontSize: '11px', fontWeight: 700, padding: '5px 10px', borderRadius: '6px', border: 'none', cursor: newOptDraft.trim() ? 'pointer' : 'not-allowed', background: newOptDraft.trim() ? '#0f172a' : '#e2e8f0', color: newOptDraft.trim() ? '#fff' : '#94a3b8', fontFamily: 'inherit' }}>
                    Add
                  </button>
                  <button onClick={() => { setNewOptDraft(''); setShowAddOpt(false); }}
                    style={{ fontSize: '11px', fontWeight: 700, padding: '5px 9px', borderRadius: '6px', border: '1px solid #e2e8f0', cursor: 'pointer', background: '#fff', color: '#64748b', fontFamily: 'inherit' }}>
                    ✕
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Joining Today panel */}
        <div style={css.panel}>
          <div style={css.panelHead}>
            <span style={css.panelTitle as React.CSSProperties}>Joining {dayView === 'today' ? 'Today' : fmtDateShort(availDate)}</span>
            <span style={{ ...css.cnt, background: '#7c3aed' }}>{joiningToday.length}</span>
          </div>
          {joiningToday.length === 0
            ? <div style={{ padding: '14px', textAlign: 'center', fontSize: '11px', color: '#94a3b8' }}>No staff joining</div>
            : joiningToday.map(n => (
              <div key={n.id} style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', gap: '10px', borderBottom: '1px solid #f8fafc' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#7c3aed', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#0f172a' }}>{(n as any).full_name}</div>
                  <div style={{ fontSize: '10px', color: '#94a3b8' }}>Joining</div>
                </div>
                <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '4px', background: '#f3e8ff', color: '#6d28d9' }}>New</span>
              </div>
            ))
          }
        </div>
      </div>
    );
  };

  // ─── Staff pool tab ──────────────────────────────────────────────────────────

  const renderPoolTab = () => {
    // Category display order for the Status column (see below). Physios (no
    // availability) sort last.
    const CAT_ORDER: Record<string, number> = {
      'Available': 0, 'On Temporary Duty': 1, 'Back-Up': 2,
      'On Duty - Shuffle Possibility': 3, 'On Duty': 4,
      'On Leave - Regular': 5, 'On Leave - Emergency': 6, 'On Leave - Sick': 7,
      'Uncertainty': 8, 'Absconded': 9, 'Resigned': 10,
    };
    // A staff member's effective status category (same rule as the Status cell).
    const catOf = (s: any): string => {
      if (s.staffType === 'Physio') return '';
      const onDuty = assignments.some(x => x.nurse?.id === s.id || x.second_nurse?.id === s.id);
      return onDuty ? 'On Duty' : (nurseAvail.find(r => r.nurse_id === s.id)?.category || DEFAULT_AVAIL);
    };
    // Name key: ignore leading spaces / non-letters so it sorts by the first LETTER.
    const nameKey = (s: any) => (s.full_name || '').trim().replace(/^[^A-Za-z]+/, '').toLowerCase();
    // On Duty sub-order by the nurse's assigned shift: 08 → 12 Dual → 12 Day → 12 Night → 24.
    const onDutyShiftRank = (s: any): number => {
      const a = assignments.find(x => x.nurse?.id === s.id || x.second_nurse?.id === s.id);
      const t = (a?.shift_type || '').toLowerCase();
      if (t.startsWith('08')) return 0;
      if (t.includes('dual')) return 1;
      if (t.includes('12 day')) return 2;
      if (t.includes('12 night')) return 3;
      if (t.includes('24')) return 4;
      return 5;
    };
    const poolQuery = poolSearch.trim().toLowerCase();
    const allStaff = [
      ...nurses.map(n => ({ ...n, staffType: 'Nurse' })),
      ...physios.map(p => ({ ...p, staffType: 'Physio' })),
    ]
      .filter(s => !poolQuery || (s.full_name || '').toLowerCase().includes(poolQuery))
      .sort((a, b) => {
        if (poolSort === 'name') return nameKey(a).localeCompare(nameKey(b));   // sort by name (A→Z)
        const ca = catOf(a), cb = catOf(b);
        const ra = ca === '' ? 99 : (CAT_ORDER[ca] ?? 50);
        const rb = cb === '' ? 99 : (CAT_ORDER[cb] ?? 50);
        if (ra !== rb) return ra - rb;                        // 1) by status priority order
        if (ca === 'Available') {                              // Available → rating desc (highest first)
          const rvA = (a as any).rating ?? -1, rvB = (b as any).rating ?? -1;
          if (rvA !== rvB) return rvB - rvA;
        } else if (ca === 'On Duty') {                         // On Duty → by shift type
          const sa = onDutyShiftRank(a), sb = onDutyShiftRank(b);
          if (sa !== sb) return sa - sb;
        }
        return nameKey(a).localeCompare(nameKey(b));           // 2) A→Z within the group
      });
    const th = (label: string, center = false) => (
      <th key={label} style={{ padding: '0.55rem 0.6rem', background: '#86afd8', color: '#1e3a5f', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.03em', border: '1px solid #c5cbd3', textAlign: center ? 'center' : 'left', whiteSpace: 'nowrap' }}>{label}</th>
    );
    const cell: React.CSSProperties = { padding: '8px 10px', fontSize: '11px', color: '#334155', border: '1px solid #c5cbd3', verticalAlign: 'top' };
    const cols: [string, boolean][] = [
      ['Sl.No', true], ['Photo', true], ['Name', false], ['Age/Sex', true], ['Qualification', false],
      ['Reg', true], ['Exp', false], ['Dept', false], ['Hospital(s)', false], ['Languages', false],
      ['Relig / Marital', false], ['State / Dist', false], ['A', true], ['D', true], ['Contact', false],
      ['Status', false], ['Current Case', false], ['Rating', true], ['View', true],
    ];
    return (
      <div style={{ padding: '12px 20px' }}>
        {/* Toolbar: search by name + sort */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', width: '280px', maxWidth: '100%' }}>
            <svg width="13" height="13" fill="none" stroke="#94a3b8" viewBox="0 0 24 24" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }}>
              <circle cx="11" cy="11" r="7" strokeWidth="2" /><path d="m21 21-4.3-4.3" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input value={poolSearch} onChange={e => setPoolSearch(e.target.value)} placeholder="Search staff by name…"
              style={{ width: '100%', padding: '7px 26px 7px 30px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' }} />
            {poolSearch && (
              <button onClick={() => setPoolSearch('')} title="Clear"
                style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: '14px', lineHeight: 1 }}>×</button>
            )}
          </div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 700, color: '#64748b' }}>
            SORT BY
            <select value={poolSort} onChange={e => setPoolSort(e.target.value as 'status' | 'name')}
              style={{ fontSize: '12px', fontWeight: 600, color: '#334155', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 8px', background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
              <option value="status">Status (priority order)</option>
              <option value="name">Name (A–Z)</option>
            </select>
          </label>
          <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>{allStaff.length} staff</span>
        </div>

        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', color: '#1d2631', minWidth: '1500px' }}>
            <thead><tr>{cols.map(([label, c]) => th(label, c))}</tr></thead>
            <tbody>
              {allStaff.length === 0 && <tr><td colSpan={cols.length} style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>No staff data</td></tr>}
              {allStaff.map((s: any, index: number) => {
                const stKey: 'nurse' | 'physio' = s.staffType === 'Physio' ? 'physio' : 'nurse';
                // Status: "On Duty" when assigned to a case on the active day, else the
                // nurse's availability category (physios have no availability → '—').
                const onDuty = assignments.some(x => x.nurse?.id === s.id || x.second_nurse?.id === s.id);
                const availCat = stKey === 'nurse' ? (onDuty ? 'On Duty' : (nurseAvail.find(r => r.nurse_id === s.id)?.category || DEFAULT_AVAIL)) : '';
                const ratingVal: number | null = (s.rating ?? null);
                const currentCase = getAssignedCase(s.id);
                return (
                  <tr key={`${s.staffType}-${s.id}`}
                    onMouseEnter={e => { Array.from(e.currentTarget.cells).forEach(c => (c.style.background = '#f8fafc')); }}
                    onMouseLeave={e => { Array.from(e.currentTarget.cells).forEach(c => (c.style.background = '')); }}>
                    {/* Sl.No */}
                    <td style={{ ...cell, textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>{index + 1}</td>
                    {/* Photo */}
                    <td style={{ ...cell, textAlign: 'center' }}>
                      <ZoomableImage src={getPoolImageUrl(s.photo_url)} alt={`${s.full_name} photo`}
                        style={{ width: '46px', height: '46px', borderRadius: '5px', objectFit: 'cover', border: '1px solid #e2e8f0', display: 'block', margin: '0 auto' }}
                        fallbackSrc={PLACEHOLDER_IMG} />
                    </td>
                    {/* Name */}
                    <td style={{ ...cell, fontWeight: 700, color: '#0f172a', fontSize: '12px', whiteSpace: 'nowrap' }}>
                      <div>{s.full_name}</div>
                      <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 400 }}>{s.staffType}</div>
                    </td>
                    {/* Age/Sex */}
                    <td style={{ ...cell, textAlign: 'center', whiteSpace: 'nowrap' }}>{poolCalcAge(s.dob)}/{s.sex?.[0] ?? '-'}</td>
                    {/* Qualification */}
                    <td style={cell}>{poolQualifications(s.qualifications)}</td>
                    {/* Reg */}
                    <td style={{ ...cell, textAlign: 'center' }}><PoolTC v={!!(s.registrations?.length)} /></td>
                    {/* Exp */}
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>{s.total_experience || '-'}</td>
                    {/* Dept */}
                    <td style={cell}>{s.departments || '-'}</td>
                    {/* Hospitals */}
                    <td style={cell}>
                      {s.work_experiences?.length
                        ? <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>{s.work_experiences.map((w: any, i: number) => <span key={i}>{w.institution_name}</span>)}</div>
                        : '-'}
                    </td>
                    {/* Languages */}
                    <td style={cell}>{s.languages?.length ? s.languages.map((l: any) => l.language).join(', ') : '-'}</td>
                    {/* Relig / Marital */}
                    <td style={cell}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                        <span>{s.religion || '-'}</span>
                        <span style={{ color: '#64748b', fontSize: '0.64rem', backgroundColor: '#f1f5f9', padding: '1px 4px', borderRadius: '999px', width: 'fit-content' }}>{s.marital_status || '–'}</span>
                      </div>
                    </td>
                    {/* State / Dist */}
                    <td style={cell}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                        <span style={{ fontWeight: 600 }}>{s.native_state || '-'}</span>
                        <span style={{ color: '#64748b', fontSize: '0.64rem' }}>{s.native_district || ''}</span>
                      </div>
                    </td>
                    {/* A — Aadhar */}
                    <td style={{ ...cell, textAlign: 'center' }}><PoolTC v={!!s.aadhar_number} /></td>
                    {/* D — Documents */}
                    <td style={{ ...cell, textAlign: 'center' }}><PoolTC v={s.document_status === 'Full Set Documents'} /></td>
                    {/* Contact */}
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>{poolContacts(s)}</td>
                    {/* Status — On Duty when assigned, else availability category.
                        Editable (nurses): click the pill → availability dropdown. */}
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                      {availCat ? (() => {
                        const editable = stKey === 'nurse' && isEditable;
                        return (
                          <span
                            onClick={editable ? (e) => {
                              e.stopPropagation();
                              if (availMenuId === s.id) { setAvailMenuId(null); return; }
                              availAnchorRef.current = e.currentTarget as HTMLElement;
                              const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              setAvailMenuPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left, window.innerWidth - 186)) });
                              setAvailMenuId(s.id);
                            } : undefined}
                            title={editable ? 'Change availability' : undefined}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', background: '#fff', color: AVAIL_CAT_COLOR(availCat), border: `1px solid ${AVAIL_CAT_COLOR(availCat)}55`, cursor: editable ? 'pointer' : 'default' }}
                          >
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: AVAIL_CAT_COLOR(availCat) }} />{availCat}
                            {editable && <span style={{ fontSize: '8px', opacity: 0.7 }}>▾</span>}
                          </span>
                        );
                      })() : <span style={{ color: '#cbd5e1', fontSize: '11px' }}>—</span>}

                      {availMenuId === s.id && availMenuPos && createPortal(
                        <div ref={availMenuRef} style={{ position: 'fixed', top: availMenuPos.top, left: availMenuPos.left, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.14)', zIndex: 9999, minWidth: '170px', maxHeight: '280px', overflowY: 'auto', padding: '4px' }}>
                          <div style={{ fontSize: '9px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '4px 12px 2px' }}>Availability</div>
                          {availOptions.map(opt => {
                            const isSel = availCat === opt;
                            const c = AVAIL_CAT_COLOR(opt);
                            return (
                              <div key={opt} onClick={() => { setAvailability(s.id, opt); setAvailMenuId(null); }}
                                style={{ padding: '7px 12px', fontSize: '11px', fontWeight: isSel ? 700 : 600, color: isSel ? c : '#334155', background: isSel ? `${c}14` : 'transparent', cursor: 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}
                                onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}>
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: c, flexShrink: 0 }} />
                                {opt}
                              </div>
                            );
                          })}
                        </div>,
                        document.body
                      )}
                    </td>
                    {/* Current Case */}
                    <td style={cell}>{currentCase}</td>
                    {/* Rating (1–10) */}
                    <td style={{ ...cell, textAlign: 'center' }}>
                      <select value={ratingVal ?? ''} disabled={!isEditable}
                        onChange={e => setStaffRating(s.id, stKey, e.target.value === '' ? null : Number(e.target.value))}
                        style={{ fontSize: '11px', fontWeight: 700, color: ratingVal ? '#d97706' : '#94a3b8', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '3px 6px', background: isEditable ? '#fff' : '#f8fafc', cursor: isEditable ? 'pointer' : 'default', fontFamily: 'inherit', outline: 'none' }}>
                        <option value="">— rate —</option>
                        {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(v => <option key={v} value={v}>★ {v}/10</option>)}
                      </select>
                    </td>
                    {/* View — opens the staff member's profile */}
                    <td style={{ ...cell, textAlign: 'center' }}>
                      <button onClick={() => stKey === 'nurse' ? setViewNurseId(s.id) : window.alert('Physio profile view is not available yet.')}
                        style={{ fontSize: '10px', fontWeight: 700, padding: '4px 11px', borderRadius: '6px', background: 'linear-gradient(135deg,#6366f1 0%,#4f46e5 100%)', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ─── Leave tracker tab ───────────────────────────────────────────────────────

  // Per-category card colors (each card keeps its own colour inside a column).
  const CAT_CFG: Record<string, { bg: string; color: string; border: string; dot: string }> = {
    'On Leave - Regular':   { bg: '#fffbeb', color: '#92400e', border: '#fde68a', dot: '#f59e0b' },
    'On Leave - Emergency': { bg: '#fef2f2', color: '#b91c1c', border: '#fca5a5', dot: '#dc2626' },
    'On Leave - Sick':      { bg: '#fff7ed', color: '#c2410c', border: '#fdba74', dot: '#f97316' },
  };

  // Leave board columns — one per leave type (Regular, Emergency, Sick).
  // `dropCat` = category stamped when a card is dropped into the column.
  const LEAVE_COLS = [
    { key: 'regular',   label: 'On Leave - Regular',   cats: ['On Leave - Regular'],   dropCat: 'On Leave - Regular',
      bg: '#fffbeb', color: '#92400e', border: '#fde68a', dot: '#f59e0b', cnt: '#d97706' },
    { key: 'emergency', label: 'On Leave - Emergency', cats: ['On Leave - Emergency'], dropCat: 'On Leave - Emergency',
      bg: '#fef2f2', color: '#b91c1c', border: '#fca5a5', dot: '#dc2626', cnt: '#dc2626' },
    { key: 'sick',      label: 'On Leave - Sick',      cats: ['On Leave - Sick'],      dropCat: 'On Leave - Sick',
      bg: '#fff7ed', color: '#c2410c', border: '#fdba74', dot: '#f97316', cnt: '#ea580c' },
  ];

  const renderLeaveTab = () => {
    const lr = leaveRangeFor(availDate, leaveView);
    const rangeLabel = leaveView === 'daily' ? fmtDateShort(lr.start) : `${fmtDateShort(lr.start)} → ${fmtDateShort(lr.end)}`;
    return (
      <div style={{ padding: '12px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
            Leave Management · {rangeLabel}
            <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500, marginLeft: '8px' }}>
              {isEditable ? 'drag to change status · click a card to edit dates' : '(read-only)'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Daily / Weekly / Monthly range view */}
            <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: '8px', padding: '2px' }}>
              {(['daily', 'weekly', 'monthly'] as const).map(v => (
                <button key={v} onClick={() => setLeaveView(v)}
                  style={{ padding: '5px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 700, fontFamily: 'inherit', textTransform: 'capitalize',
                    background: leaveView === v ? '#fff' : 'transparent', color: leaveView === v ? '#1d4ed8' : '#64748b', boxShadow: leaveView === v ? '0 1px 3px rgba(15,23,42,0.12)' : 'none' }}>
                  {v}
                </button>
              ))}
            </div>
            {isEditable && (
              <button onClick={() => { setLeaveEdit(null); setLeaveForm({ nurse_id: '', category: 'On Leave - Regular', from_date: lr.start, to_date: lr.start }); setLeaveModal(true); }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff', border: 'none', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                <span style={{ fontSize: '15px', lineHeight: 1 }}>+</span> Add Leave
              </button>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${LEAVE_COLS.length},1fr)`, gap: '14px', alignItems: 'start' }}>
          {LEAVE_COLS.map(col => {
            const items = leavePeriods
              .filter(r => col.cats.includes(r.category))
              .sort((a, b) => col.cats.indexOf(a.category) - col.cats.indexOf(b.category)); // Emergency before Planned
            const dragging = dragCard != null;
            const isOver = dragging && hoverCol === col.key;
            return (
              <div key={col.key}
                onDragOver={(e) => { if (isEditable && dragging) { e.preventDefault(); if (hoverCol !== col.key) setHoverCol(col.key); } }}
                onDrop={() => {
                  if (!isEditable) return;
                  // Dropping into the merged Leave column keeps the card's leave sub-type if it
                  // already had one; otherwise it becomes the column's default category.
                  const target = (col.key === 'leave' && dragCard && col.cats.includes(dragCard.category))
                    ? dragCard.category : col.dropCat;
                  dropLeave(target);
                }}
                style={{
                  background: '#fff', borderRadius: '14px', overflow: 'hidden',
                  border: `1.5px ${isOver ? 'dashed' : 'solid'} ${isOver ? col.dot : (dragging ? col.border : '#e2e8f0')}`,
                  boxShadow: isOver ? `0 14px 30px -12px ${col.dot}66` : '0 1px 3px rgba(0,0,0,0.04)',
                  transform: isOver ? 'translateY(-3px)' : 'none',
                  transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
                }}>
                <div style={{ padding: '11px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `2px solid ${col.border}`, background: col.bg }}>
                  <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: col.color }}>{col.label}</span>
                  <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', background: col.cnt, color: '#fff' }}>{items.length}</span>
                </div>

                <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '8px', minHeight: '90px' }}>
                  {items.length === 0 && (
                    <div style={{ flex: 1, minHeight: '74px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px', border: `1.5px dashed ${isOver ? col.dot : '#e2e8f0'}`, background: isOver ? col.bg : '#fff', fontSize: '11px', fontWeight: 600, color: isOver ? col.color : '#cbd5e1', transition: 'all .15s ease' }}>
                      {isOver ? 'Drop to move here' : 'No staff'}
                    </div>
                  )}
                  {items.map((r, idx) => {
                    const cc = CAT_CFG[r.category] || col;   // each card keeps its own colour
                    // In the merged Leave column, show a sub-label when the sub-type changes.
                    const showSubLabel = col.key === 'leave' && (idx === 0 || items[idx - 1].category !== r.category);
                    const beingDragged = !!dragCard && dragCard.nurse_id === r.nurse_id && dragCard.from_date === r.from_date && dragCard.to_date === r.to_date && dragCard.category === r.category;
                    const rangeText = (r.from_date && r.to_date)
                      ? (r.from_date === r.to_date ? fmtDateShort(r.from_date) : `${fmtDateShort(r.from_date)} – ${fmtDateShort(r.to_date)}`)
                      : fmtDateShort(availDate);
                    const sub = `${r.designation || 'Nurse'}${r.clinical_class ? ` · ${getNurseSkill(r)}` : ''}`;
                    return (
                      <React.Fragment key={`${r.nurse_id}-${r.from_date}-${r.to_date}`}>
                        {showSubLabel && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '9px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: cc.color, marginTop: idx === 0 ? 0 : '2px' }}>
                            <span style={{ width: '7px', height: '7px', borderRadius: '999px', background: cc.dot }} />
                            {r.category}
                          </div>
                        )}
                        <div
                          draggable={isEditable}
                          onClick={() => { if (isEditable) openEditLeave(r); }}
                          title={isEditable ? 'Click to edit dates / type' : undefined}
                          onDragStart={(e) => { setDragCard(r); buildLeaveDragImage(e, r.full_name, sub, cc.dot); }}
                          onDragEnd={() => { setDragCard(null); setHoverCol(null); }}
                          onMouseEnter={(e) => { if (!beingDragged && isEditable) { e.currentTarget.style.boxShadow = '0 6px 16px -6px rgba(15,23,42,0.22)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                          onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,23,42,0.06)'; e.currentTarget.style.transform = 'none'; }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 10px', borderRadius: '10px',
                            background: beingDragged ? '#f8fafc' : '#fff',
                            border: beingDragged ? '1.5px dashed #cbd5e1' : '1px solid #eef1f6',
                            borderLeft: beingDragged ? '1.5px dashed #cbd5e1' : `3px solid ${cc.dot}`,
                            boxShadow: beingDragged ? 'none' : '0 1px 2px rgba(15,23,42,0.06)',
                            opacity: beingDragged ? 0.5 : 1,
                            cursor: isEditable ? 'pointer' : 'default',
                            transition: 'box-shadow .15s ease, transform .15s ease',
                          }}>
                          <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: cc.dot, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '12px', flexShrink: 0 }}>{(r.full_name || '?')[0]}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.full_name}</div>
                            <div style={{ fontSize: '10px', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>
                            <div style={{ fontSize: '10px', fontWeight: 700, color: cc.color, marginTop: '3px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
                              {rangeText}
                            </div>
                          </div>
                          {isEditable && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                              {/* On the Returning column, an explicit "mark available" action
                                  (sends them to Ready Available, off the leave board). */}
                              {col.key === 'returning' && (
                                <button onClick={(e) => { e.stopPropagation(); removeFromLeave(r); }} title="Mark available (back to Available)"
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#16a34a', cursor: 'pointer', fontSize: '10px', fontWeight: 800, padding: '3px 8px', borderRadius: '999px', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                                  onMouseEnter={e => { e.currentTarget.style.background = '#dcfce7'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = '#f0fdf4'; }}>
                                  ✓ Available
                                </button>
                              )}
                              <button onClick={(e) => { e.stopPropagation(); removeFromLeave(r); }} title="Remove from leave (back to available)"
                                style={{ border: 'none', background: 'transparent', color: '#cbd5e1', cursor: 'pointer', fontSize: '14px', lineHeight: 1 }}>×</button>
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                  {/* Quick-register a brand-new nurse straight into the New Joinee column */}
                  {col.key === 'newjoinee' && isEditable && (
                    <button onClick={() => setIsAddNurseOpen(true)}
                      style={{ width: '100%', border: '1px dashed #ddd6fe', background: '#faf5ff', color: '#6d28d9', borderRadius: '10px', padding: '8px', fontSize: '11px', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f3e8ff'; e.currentTarget.style.borderColor = '#7c3aed'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#faf5ff'; e.currentTarget.style.borderColor = '#ddd6fe'; }}>
                      <span style={{ fontSize: '14px', lineHeight: 1 }}>+</span> Add New Joinee
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Add / Edit Leave modal */}
        {leaveModal && (
          <div onClick={() => { setLeaveModal(false); setLeaveEdit(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '14px', width: '400px', maxWidth: '92vw', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#fafafa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{leaveEdit ? 'Edit Leave' : 'Add Leave'}</div>
                <button onClick={() => { setLeaveModal(false); setLeaveEdit(null); }} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '20px', color: '#94a3b8', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Staff member
                  {leaveEdit ? (
                    <input value={nurses.find((s: any) => String(s.id) === leaveForm.nurse_id)?.full_name || ''} disabled
                      style={{ ...fbInput, background: '#f8fafc', color: '#64748b' }} />
                  ) : (
                    <SearchableSelect
                      value={leaveForm.nurse_id}
                      display={nurses.find((s: any) => String(s.id) === leaveForm.nurse_id)?.full_name || ''}
                      placeholder="Search staff by name…"
                      options={nurses.map((s: any) => ({ id: String(s.id), label: s.full_name || `#${s.id}` }))}
                      onSelect={(id) => setLeaveForm(f => ({ ...f, nurse_id: id }))}
                    />
                  )}
                </label>
                <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Leave type
                  <select value={leaveForm.category} onChange={(e) => setLeaveForm(f => ({ ...f, category: e.target.value }))} style={fbInput}>
                    {[
                      { cat: 'On Leave - Regular',   label: 'On Leave - Regular' },
                      { cat: 'On Leave - Emergency', label: 'On Leave - Emergency' },
                      { cat: 'On Leave - Sick',      label: 'On Leave - Sick' },
                    ].map(c => <option key={c.cat} value={c.cat}>{c.label}</option>)}
                  </select>
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <label style={{ flex: 1, fontSize: '11px', fontWeight: 600, color: '#475569' }}>From
                    <input type="date" value={leaveForm.from_date}
                      onChange={(e) => setLeaveForm(f => ({ ...f, from_date: e.target.value, to_date: (f.to_date && f.to_date >= e.target.value) ? f.to_date : e.target.value }))}
                      style={fbInput} />
                  </label>
                  <label style={{ flex: 1, fontSize: '11px', fontWeight: 600, color: '#475569' }}>To
                    <input type="date" value={leaveForm.to_date} min={leaveForm.from_date}
                      onChange={(e) => setLeaveForm(f => ({ ...f, to_date: e.target.value }))}
                      style={fbInput} />
                  </label>
                </div>
                <div style={{ fontSize: '10px', color: '#94a3b8' }}>Leave applies to every day in this range.</div>
              </div>
              <div style={{ padding: '12px 18px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button onClick={() => { setLeaveModal(false); setLeaveEdit(null); }} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                <button onClick={addLeave} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>{leaveEdit ? 'Save Changes' : 'Add Leave'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Upcoming cases tab ──────────────────────────────────────────────────────

  const renderUpcomingTab = () => {
    const upcoming = [...allPatients]
      .filter(p => {
        const start = (p as any).case_start_date;
        if (!start) return false;
        const d = new Date(String(start).slice(0, 10));
        const today = new Date(selectedDate);
        return d >= today;
      })
      .sort((a, b) => new Date((a as any).case_start_date).getTime() - new Date((b as any).case_start_date).getTime())
      .slice(0, 15);
    const matches = ucSelected ? [] : ucResults;
    const showAddClient = ucName.trim().length >= 2 && !ucSelected && matches.length === 0;
    return (
      <div style={{ padding: '12px 20px' }}>
        {/* ── Stage a new upcoming case ── */}
        <div style={{ width: '300px', maxWidth: '100%', background: '#fff', border: '1px dashed #c7d2fe', borderRadius: '12px', padding: '11px 12px', marginBottom: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ fontSize: '10px', fontWeight: 800, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>New Upcoming Case</div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* Client name + search */}
            <div style={{ position: 'relative', flex: '1 1 100%', minWidth: 0 }}>
              <input
                value={ucName}
                onChange={e => ucSearch(e.target.value)}
                placeholder="Client name…"
                style={{ width: '100%', padding: '8px 10px', fontSize: '12px', border: `1px solid ${ucSelected ? '#86efac' : '#e2e8f0'}`, borderRadius: '8px', boxSizing: 'border-box', fontFamily: 'inherit', background: ucSelected ? '#f0fdf4' : '#fff' }}
              />
              {ucSelected && (
                <button onClick={ucReset} title="Clear"
                  style={{ position: 'absolute', right: '6px', top: '6px', border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: '14px', lineHeight: 1 }}>×</button>
              )}
              {/* Existing-client matches */}
              {matches.length > 0 && (
                <div style={{ position: 'absolute', top: '38px', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, overflow: 'hidden' }}>
                  {matches.map(p => (
                    <div key={p.id} onClick={() => ucSelectClient(p)}
                      style={{ padding: '8px 10px', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                      onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
                      <span style={{ fontWeight: 700, color: '#0f172a' }}>{getClientName(p) || p.patient_name}</span>
                      {getClientName(p) && <span style={{ color: '#94a3b8', marginLeft: '6px' }}>· {p.patient_name}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add new client (when no match) */}
            {showAddClient && (
              <button onClick={() => window.open('/patients?new=1', '_blank')}
                style={{ flex: '1 1 100%', padding: '7px 10px', fontSize: '11px', fontWeight: 700, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit' }}>
                + Add client
              </button>
            )}

            {/* Start date */}
            {ucSelected && (
              <input type="date" value={ucStartDate} onChange={e => setUcStartDate(e.target.value)}
                style={{ flex: '1 1 100%', padding: '7px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontFamily: 'inherit', boxSizing: 'border-box' }} />
            )}
          </div>

          {/* Shifts + create */}
          {ucSelected && (
            <div style={{ marginTop: '10px' }}>
              <div style={{ fontSize: '9px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>Shifts</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '10px' }}>
                {SHIFT_OPTIONS.map(o => {
                  const on = ucShifts.includes(o.type);
                  return (
                    <button key={o.type} onClick={() => toggleUcShift(o.type)}
                      style={{ padding: '4px 9px', fontSize: '10px', fontWeight: 700, borderRadius: '999px', cursor: 'pointer', fontFamily: 'inherit',
                        border: `1.5px solid ${on ? '#4f46e5' : '#e2e8f0'}`, background: on ? '#4f46e5' : '#fff', color: on ? '#fff' : '#475569' }}>
                      {o.label}
                    </button>
                  );
                })}
              </div>
              <button onClick={ucCreate} disabled={ucSaving || ucShifts.length === 0}
                style={{ width: '100%', padding: '8px', fontSize: '12px', fontWeight: 700, borderRadius: '8px', border: 'none', cursor: ucSaving || ucShifts.length === 0 ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  background: ucSaving || ucShifts.length === 0 ? '#cbd5e1' : 'linear-gradient(135deg,#6366f1,#4f46e5)', color: '#fff' }}>
                {ucSaving ? 'Creating…' : 'Create Upcoming Case'}
              </button>
            </div>
          )}
        </div>

        {upcoming.length === 0 && (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No upcoming cases found</div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: '10px' }}>
        {upcoming.map(p => {
          const startDate = (p as any).case_start_date;
          const startStr = String(startDate).slice(0, 10);
          // Staffing for THIS case's start date (from the forward window), not today.
          const dayList = upcomingAsgn.filter(a => String(a.start_date).slice(0, 10) === startStr);
          const shifts = getShiftsForPatient(p, dayList);
          const filled = shifts.filter(s => s.names.length > 0).length;
          const total = shifts.length;
          const pct = total > 0 ? Math.round(filled / total * 100) : 0;
          const bc = pct === 100 ? '#16a34a' : pct >= 50 ? '#f59e0b' : '#dc2626';
          const d = startDate ? new Date(startDate) : null;
          const isVip = (p as any).is_vip === true;
          const dutyCode = getDutyShortCode(getDutyLabel(p));
          const bStatus = normStatus((p as any).criticality);
          const bCfg = CRITICALITY_CFG[bStatus] || CRITICALITY_CFG[DEFAULT_CRITICALITY];
          const clientName = getClientName(p);
          const leftBorderColor = dutyCode === '12' ? '#7c3aed' : dutyCode === '24' ? '#0d9488' : '#3b82f6';
          const dayNote = boardNotes[`${p.id}|${startStr}`];
          const note = dayNote?.note ? dayNote.note.trim() : '';
          const noteCol = NOTE_COLORS[dayNote?.color || 'black'] || NOTE_COLORS.black;
          return (
            <div key={p.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', borderLeft: `4px solid ${leftBorderColor}` }}>
              {/* Header — tinted by criticality, mirrors the Daily Board job card */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: bCfg.bg, borderBottom: `1px solid ${bCfg.border}` }}>
                {/* Start-date chip */}
                <div style={{ textAlign: 'center', background: '#0f172a', color: '#fff', borderRadius: '7px', padding: '3px 6px', minWidth: '34px', flexShrink: 0, lineHeight: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 800 }}>{d ? String(d.getDate()).padStart(2, '0') : '—'}</div>
                  <div style={{ fontSize: '7px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.8, marginTop: '1px' }}>{d ? d.toLocaleString('en-GB', { month: 'short' }).toUpperCase() : ''}</div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: isVip ? 800 : 700, fontSize: '12.5px', color: isVip ? VIP_GOLD : '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {isVip && <span style={{ marginRight: '3px' }}>★</span>}{clientName || p.patient_name}
                  </div>
                  <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{clientName ? p.patient_name : ''}</div>
                </div>
                {/* VIP checkbox — patient-level, persisted (same as Daily Board) */}
                <label title="Mark as VIP client" onClick={(e) => e.stopPropagation()}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', flexShrink: 0, cursor: 'pointer', fontSize: '9px', fontWeight: 800, letterSpacing: '0.03em', padding: '2px 6px', borderRadius: '999px', border: `1.5px solid ${isVip ? '#fcd34d' : '#e2e8f0'}`, background: isVip ? '#fffbeb' : '#fff', color: isVip ? VIP_GOLD : '#94a3b8' }}>
                  <input type="checkbox" checked={isVip} onChange={(e) => setPatientVip(p, e.target.checked)}
                    style={{ width: '11px', height: '11px', accentColor: VIP_GOLD, cursor: 'pointer', margin: 0 }} />
                  VIP
                </label>
                <span style={dutyPillStyle(dutyCode)}>{dutyCode}hr</span>
              </div>

              {/* Shift rows — staffing + date-aware 🚩 flag */}
              {shifts.map((s, idx) => {
                const isFlagged = upcomingFlags.has(flagKey(p.id, startStr, s.session));
                return (
                  <div key={`${s.shiftId}-${idx}`} style={{ display: 'flex', alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid #f8fafc', gap: '8px', background: isFlagged ? '#fef9c3' : undefined }}>
                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: '52px' }}>{s.label}</span>
                    <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '3px', alignItems: 'center' }}>
                      {s.names.length > 0
                        ? s.names.map(name => (
                            <span key={name} style={{ ...nursePillStyle(true), cursor: 'pointer' }}
                              onClick={() => openAssignModal(p, s.shiftId, s.session, undefined, startStr)}>{name}</span>
                          ))
                        : <span style={nursePillStyle(false)} onClick={() => openAssignModal(p, s.shiftId, s.session, undefined, startStr)}>+ Assign</span>}
                    </div>
                    {/* Manual 🚩 — "a change may happen here soon" (this case's start date) */}
                    <button onClick={(e) => { e.stopPropagation(); toggleUpcomingFlag(p, s.session, startStr); }}
                      title={isFlagged ? 'Flag on — click to clear' : 'Flag: a change may happen here soon'}
                      style={{ flexShrink: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: '2px', borderRadius: '5px', opacity: isFlagged ? 1 : 0.4, filter: isFlagged ? 'none' : 'grayscale(1)' }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.filter = 'none'; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = isFlagged ? '1' : '0.4'; e.currentTarget.style.filter = isFlagged ? 'none' : 'grayscale(1)'; }}>
                      🚩
                    </button>
                  </div>
                );
              })}

              {/* Case note — per-day note for this case's start date */}
              <div style={{ padding: '6px 10px' }}>
                <div onClick={() => openNote(p, startStr)} title={note ? 'Click to edit note' : 'Click to add a note'}
                  style={{ cursor: 'pointer', borderRadius: '7px', padding: '6px 9px', fontSize: '11px', lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    border: `1px ${note ? 'solid' : 'dashed'} ${note ? noteCol : '#cbd5e1'}`,
                    background: note ? `${noteCol}0f` : '#fff', color: note ? noteCol : '#94a3b8', fontWeight: note ? 600 : 700 }}>
                  {note || '＋ Add note'}
                </div>
              </div>

              {/* Staffed progress */}
              <div style={{ padding: '2px 10px 8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ flex: 1, height: '4px', background: '#f1f5f9', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: bc, borderRadius: '2px' }} />
                </div>
                <span style={{ fontSize: '11px', fontWeight: 800, color: bc, flexShrink: 0 }}>{pct}% staffed</span>
              </div>
            </div>
          );
        })}
        </div>
      </div>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  if (loading) return (
    <div style={{ padding: '2rem', color: '#64748b', fontFamily: '"Inter",sans-serif', display: 'flex', alignItems: 'center', gap: '10px' }}>
      <div style={{ width: '18px', height: '18px', border: '2px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      Loading operations data…
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const TABS: { key: typeof activeTab; label: string }[] = [
    { key: 'board',    label: 'Daily Board' },
    { key: 'leave',    label: 'Leave Tracker' },
    { key: 'upcoming', label: 'Upcoming Cases' },
    { key: 'pool',     label: 'Staff Pool' },
    { key: 'feedback', label: 'Feedback' },
    { key: 'log',      label: 'Change Log' },
  ];

  // ── Top metrics (follow the date navigator — computed for the active day) ──
  const dayShiftRows = activeDayPatients.flatMap(p => getShiftsForPatient(p, activeAssignments));
  const mTotalShifts  = dayShiftRows.length;
  const mFilledShifts = dayShiftRows.filter(s => s.names.length > 0).length;
  const mOpenSlots    = mTotalShifts - mFilledShifts;
  // Changes = cards appearing on the active day's board (per-day membership;
  // regardless of when the patient was first registered).
  const mChanges      = activeDayPatients.length;
  // Active Cases = all cases with status Active across the system (not day-specific).
  const mActiveCases  = allPatients.filter(p => String((p as any).status || 'Active').toLowerCase() === 'active').length;
  // Outsourcing for the active day — its rates are a COST subtracted from sales.
  const activeOutsourcing = outsourcing.filter(o => o.date === activeDate);
  const mOutsourcing = activeOutsourcing.reduce((s, o) => s + (Number(o.rate) || 0), 0);
  // Daily sales (NET) = client charge for each assigned shift (primary + assistant) − outsourcing cost.
  const mGrossSales = activeAssignments.reduce(
    (sum, a) => sum + (Number((a as any).total_charge) || 0) + (Number((a as any).second_total_charge) || 0), 0
  );
  const mDailySales = mGrossSales - mOutsourcing;

  const INLINE_STATS: { label: string; val: number | string; color: string; onClick?: () => void }[] = [
    { label: 'Changes',          val: mChanges,     color: '#ea580c' },   // clients with a change today
    { label: 'Active Cases',     val: mActiveCases, color: '#2563eb' },   // all active clients (whole company)
    { label: 'Daily Sales',      val: `₹${Math.round(mDailySales).toLocaleString('en-IN')}`, color: '#0d9488', onClick: openDailySalesChart },  // NET of outsourcing
    { label: 'Outsourcing',      val: `−₹${Math.round(mOutsourcing).toLocaleString('en-IN')}`, color: '#e11d48' },  // cost subtracted from sales
    { label: 'Changed Shifts',   val: mTotalShifts, color: '#7c3aed' },   // shift slots among today's changes
    { label: 'Unfilled Changes', val: mOpenSlots,   color: '#dc2626' },   // of those, how many still need a nurse
  ];

  return (
    <div style={css.page}>
      <style>{`@keyframes blink{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>

      {/* ── Header with inline stats ── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 20px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', gap: 0 }}>

        {/* Title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <h1 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', margin: 0 }}>Local Operations Dashboard</h1>
        </div>

        {/* Inline stats */}
        <div style={{ display: 'flex', alignItems: 'center', borderLeft: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', margin: '0 18px', height: '100%', flexShrink: 1, overflow: 'hidden' }}>
          {INLINE_STATS.map((s, i) => (
            <div key={s.label} onClick={s.onClick}
              title={s.onClick ? 'Click to view daily sales for the whole month' : undefined}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 14px', borderRight: i < INLINE_STATS.length - 1 ? '1px solid #e2e8f0' : 'none', cursor: s.onClick ? 'pointer' : 'default', borderRadius: '6px', transition: 'background 0.15s' }}
              onMouseEnter={s.onClick ? (e) => { e.currentTarget.style.background = '#f0fdfa'; } : undefined}
              onMouseLeave={s.onClick ? (e) => { e.currentTarget.style.background = 'transparent'; } : undefined}>
              <div style={{ fontSize: '19px', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em', color: s.color, display: 'flex', alignItems: 'center', gap: '4px' }}>
                {s.val}{s.onClick && <span style={{ fontSize: '12px' }}>📊</span>}
              </div>
              <div style={{ fontSize: '9px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '3px', whiteSpace: 'nowrap' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Date nav — arrows to step, click the date to pick from a calendar */}
        <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', flexShrink: 0 }}>
          <button onClick={() => changeDay(-1)} style={{ background: '#f8fafc', border: 'none', padding: '7px 10px', cursor: 'pointer', color: '#64748b', fontSize: '15px', lineHeight: 1, fontFamily: 'inherit' }}>‹</button>
          <span
            onClick={() => { const el = dateInputRef.current as any; if (el?.showPicker) { try { el.showPicker(); } catch { el.focus(); } } else el?.focus(); }}
            title="Click to pick a date"
            style={{ position: 'relative', padding: '7px 14px', fontSize: '12px', fontWeight: 700, color: '#334155', borderLeft: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {fmtDateLong(selectedDate)}
            <span style={{ fontSize: '12px', lineHeight: 1 }}>📅</span>
            <input
              ref={dateInputRef}
              type="date"
              value={selectedDate}
              onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
              style={{ position: 'absolute', left: 0, bottom: 0, width: 1, height: 1, opacity: 0, border: 0, padding: 0, pointerEvents: 'none' }}
            />
          </span>
          <button onClick={() => changeDay(1)} style={{ background: '#f8fafc', border: 'none', padding: '7px 10px', cursor: 'pointer', color: '#64748b', fontSize: '15px', lineHeight: 1, fontFamily: 'inherit' }}>›</button>
        </div>

        {/* Live badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '14px', flexShrink: 0 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#f0fdf4', color: '#15803d', border: '1.5px solid #86efac', fontSize: '10px', fontWeight: 700, padding: '4px 10px', borderRadius: '999px', letterSpacing: '0.04em' }}>
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#22c55e', animation: 'blink 2s infinite' }} />
            LIVE
          </div>
        </div>

      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', padding: '0 20px', background: '#fff', borderBottom: '2px solid #e2e8f0', overflowX: 'auto' }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: '12px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '11px', fontWeight: 700, fontFamily: 'inherit',
              color: activeTab === t.key ? '#2563eb' : '#94a3b8',
              borderBottom: `2px solid ${activeTab === t.key ? '#2563eb' : 'transparent'}`,
              marginBottom: '-2px', textTransform: 'uppercase', letterSpacing: '0.05em',
              whiteSpace: 'nowrap', transition: 'color 0.15s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab content ── */}

      {activeTab === 'board' && (
        <div>
          {/* Day toggle: Yesterday (read-only) | Today | Tomorrow (both editable, shared data) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px 0' }}>
            {([
              { key: 'yesterday', label: 'Yesterday' },
              { key: 'today',     label: 'Today' },
              { key: 'tomorrow',  label: 'Tomorrow' },
            ] as const).map(v => {
              const active = dayView === v.key;
              return (
                <button key={v.key} onClick={() => setDayView(v.key)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1px',
                    padding: '7px 14px', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit',
                    border: `1px solid ${active ? '#2563eb' : '#e2e8f0'}`,
                    background: active ? '#2563eb' : '#fff',
                    color: active ? '#fff' : '#475569',
                    boxShadow: active ? '0 1px 3px rgba(37,99,235,0.3)' : 'none',
                  }}>
                  <span style={{ fontSize: '12px', fontWeight: 700 }}>
                    {v.label}{v.key === 'yesterday' ? ' (read-only)' : ''}
                  </span>
                  <span style={{ fontSize: '9px', fontWeight: 600, opacity: 0.85 }}>
                    {fmtDateShort(v.key === 'yesterday' ? addDays(selectedDate, -1) : v.key === 'tomorrow' ? addDays(selectedDate, 1) : selectedDate)}
                  </span>
                </button>
              );
            })}

            {/* Client / case search — next to the Tomorrow button */}
            <div style={{ position: 'relative', marginLeft: '8px', width: '240px', flexShrink: 0 }}>
              <svg width="14" height="14" fill="none" stroke="#94a3b8" viewBox="0 0 24 24" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input value={boardSearch} onChange={e => setBoardSearch(e.target.value)}
                placeholder="Search client or patient…"
                style={{ width: '100%', padding: '8px 28px 8px 30px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', fontFamily: 'inherit', outline: 'none', color: '#0f172a', background: '#fff', boxSizing: 'border-box' }} />
              {boardSearch && (
                <button onClick={() => setBoardSearch('')} title="Clear" style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: '14px', lineHeight: 1 }}>×</button>
              )}
            </div>

            {/* Add Case — editable days only. Yesterday's board is fully read-only:
                cases can't be added, removed, or edited there. */}
            {isEditable && (
              <button
                onClick={() => setAddingCase(true)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: '#fff', border: 'none',
                  borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                  fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(37,99,235,0.25)', whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
              >
                <span style={{ fontSize: '15px', lineHeight: 1 }}>+</span> Add Case
              </button>
            )}

            {!isEditable && (
              <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '4px' }}>
                Viewing a past day — assignments are read-only.
              </span>
            )}
          </div>

          <div style={css.boardWrap}>
            <div>
              {dayView === 'tomorrow' ? (
                <>
                  {/* TOMORROW section — drop target */}
                  <div
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (!tmrwOver) setTmrwOver(true); }}
                    onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setTmrwOver(false); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const id = dragTodayId ?? (Number(e.dataTransfer.getData('text/plain')) || null);
                      if (id != null) carryToTomorrow(id);
                      setDragTodayId(null); setTmrwOver(false);
                    }}
                    style={{
                      borderRadius: '12px', padding: '10px', marginBottom: '8px',
                      border: `2px dashed ${tmrwOver ? '#2563eb' : 'transparent'}`,
                      background: tmrwOver ? '#eff6ff' : 'transparent', transition: 'all .15s ease',
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Tomorrow · {fmtDateShort(activeDate)}</span>
                      <span style={{ fontSize: '11px', color: tmrwOver ? '#1d4ed8' : '#94a3b8', fontWeight: 600 }}>
                        {tmrwOver ? 'Drop to copy today’s staffing →' : 'drop a Today case here to copy its staffing'}
                      </span>
                    </div>
                    <div style={css.casesGrid}>
                      {visiblePatients.map((p, i) => renderCaseCard(p, i + 1))}
                      {!boardSearch.trim() && isEditable && renderAddCaseCard()}
                    </div>
                  </div>

                  {/* Outsourcing section (for Tomorrow) */}
                  {renderOutsourcingSection()}

                  {/* TODAY section — drag source */}
                  <div style={{ marginTop: '14px', borderTop: '1px solid #e2e8f0', paddingTop: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Today · {fmtDateShort(selectedDate)}</span>
                      <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>drag a case up into Tomorrow to copy its staffing · × to hide a suggestion</span>
                      {hiddenTodaySrcIds.size > 0 && (
                        <button onClick={() => setHiddenTodaySrcIds(new Set())}
                          style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: 700, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '999px', padding: '3px 10px', cursor: 'pointer', fontFamily: 'inherit' }}>
                          Show {hiddenTodaySrcIds.size} hidden
                        </button>
                      )}
                    </div>
                    <div style={css.casesGrid}>
                      {todayStripPatients.filter(p => !hiddenTodaySrcIds.has(p.id)).map((p, i) => renderTodaySourceCard(p, i + 1))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div style={css.casesGrid}>
                    {visiblePatients.map((p, i) => renderCaseCard(p, i + 1))}
                    {!boardSearch.trim() && isEditable && renderAddCaseCard()}
                  </div>
                  {visiblePatients.length === 0 && (
                    <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                      {boardSearch.trim() ? `No cases match “${boardSearch}”.` : 'No cases on this day’s board. Use the + card to add one.'}
                    </div>
                  )}

                  {/* Outsourcing section */}
                  {renderOutsourcingSection()}

                  {/* Collapsible YESTERDAY reference strip — only in the Today view */}
                  {dayView === 'today' && (
                    <div style={{ marginTop: '14px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
                      <div onClick={() => setShowYesterday(v => !v)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', padding: '4px 2px' }}>
                        <span style={{ display: 'inline-flex', color: showYesterday ? '#2563eb' : '#94a3b8', transform: showYesterday ? 'none' : 'rotate(-90deg)', transition: 'transform .15s' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" /></svg>
                        </span>
                        <span style={{ fontSize: '12px', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Yesterday · {fmtDateShort(addDays(selectedDate, -1))}</span>
                        <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>{yesterdayBoardPatients.length} cases · {showYesterday ? 'click to hide' : 'click to view previous day'}</span>
                        {showYesterday && hiddenYesterdayIds.size > 0 && (
                          <button onClick={(e) => { e.stopPropagation(); setHiddenYesterdayIds(new Set()); }}
                            style={{ marginLeft: '4px', fontSize: '10px', fontWeight: 700, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '999px', padding: '3px 10px', cursor: 'pointer', fontFamily: 'inherit' }}>
                            Show {hiddenYesterdayIds.size} hidden
                          </button>
                        )}
                      </div>
                      {showYesterday && (() => {
                        const shown = yesterdayBoardPatients.filter(p => !hiddenYesterdayIds.has(p.id));
                        return (
                          <div style={{ ...css.casesGrid, marginTop: '8px' }}>
                            {/* Exact mirror of the real Yesterday board — yesterday's cases with yesterday's staffing */}
                            {shown.map((p, i) => renderRefCard(p, yesterdayAsgn, i + 1,
                              () => setHiddenYesterdayIds(prev => { const n = new Set(prev); n.add(p.id); return n; })))}
                            {shown.length === 0 && (
                              <div style={{ padding: '1.5rem', color: '#94a3b8', fontSize: '12px' }}>
                                {yesterdayBoardPatients.length === 0 ? 'No changes on yesterday’s board.' : 'All yesterday cards hidden.'}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </>
              )}
            </div>
            <div style={{ position: 'sticky', top: '116px' }}>
              {renderAvailPanel()}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'leave' && renderLeaveTab()}

      {activeTab === 'upcoming' && renderUpcomingTab()}

      {activeTab === 'pool' && renderPoolTab()}

      {activeTab === 'feedback' && (() => {
        // Resolve client name per patient (for searching by client, not patient).
        const clientById = new Map<number, string>();
        fbAllPatients.forEach(p => clientById.set(p.id, getClientName(p)));
        const fq = fbSearch.trim().toLowerCase();
        const entryClient = (e: any) => (e.patient_id != null ? (clientById.get(e.patient_id) || '') : '');
        // When searching: keep only entries whose CLIENT name matches, drop empty cards.
        const visibleFeedback = !fq ? feedback : feedback
          .map((card: any) => ({ ...card, entries: (card.entries || []).filter((e: any) => entryClient(e).toLowerCase().includes(fq)) }))
          .filter((card: any) => card.entries.length > 0);
        return (
        <div style={{ padding: '12px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>Client Feedback &amp; Ratings</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ position: 'relative' }}>
                <svg width="13" height="13" fill="none" stroke="#94a3b8" viewBox="0 0 24 24" style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input value={fbSearch} onChange={e => setFbSearch(e.target.value)} placeholder="Search by client name…"
                  style={{ padding: '7px 26px 7px 28px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px', fontFamily: 'inherit', outline: 'none', color: '#0f172a', width: '220px' }} />
                {fbSearch && (
                  <button onClick={() => setFbSearch('')} title="Clear" style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: '14px', lineHeight: 1 }}>×</button>
                )}
              </div>
              <button onClick={openFeedbackModal} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff', border: 'none', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                <span style={{ fontSize: '15px', lineHeight: 1 }}>+</span> Add Feedback
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(290px,1fr))', gap: '12px' }}>
            {visibleFeedback.map((card) => {
              const r: number | null = card.rating;
              const isVip = r != null && r >= 4.5;
              const stars = r != null ? '★'.repeat(Math.floor(r)) + '☆'.repeat(5 - Math.floor(r)) : '—';
              return (
                <div key={`${card.staff_type}-${card.staff_id}`} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', position: 'relative' }}>
                  <button onClick={() => deleteFeedbackCard(card)} title="Delete feedback"
                    style={{ position: 'absolute', top: '10px', right: '10px', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', border: '1px solid #fecaca', background: '#fff1f2', color: '#dc2626', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: 0 }}>×</button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', paddingRight: '24px' }}>
                    <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#0f172a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '14px', flexShrink: 0 }}>
                      {(card.staff_name || '?')[0]}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{card.staff_name || `Staff #${card.staff_id}`}</span>
                        {isVip && <span style={{ fontSize: '9px', fontWeight: 700, padding: '1px 5px', borderRadius: '4px', background: '#fef9c3', color: '#92400e', border: '1px solid #fde68a', flexShrink: 0 }}>VIP Eligible</span>}
                        <span style={{ fontSize: '8px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>{card.staff_type}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                        <span style={{ color: '#f59e0b', fontSize: '13px' }}>{stars}</span>
                        {/* manual rating editor */}
                        <select
                          value={r ?? ''}
                          onChange={(e) => setStaffRating(card.staff_id, card.staff_type, e.target.value === '' ? null : Number(e.target.value))}
                          style={{ fontSize: '10px', border: '1px solid #e2e8f0', borderRadius: '5px', padding: '1px 3px', color: '#64748b', cursor: 'pointer' }}
                          title="Set rating"
                        >
                          <option value="">—</option>
                          {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map(v => <option key={v} value={v}>{v.toFixed(1)}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '8px' }}>
                    {card.entries.map((e: any) => (
                      <div key={e.id} style={{ padding: '7px 0', borderBottom: '1px solid #f8fafc' }}>
                        <div style={{ fontWeight: 700, fontSize: '12px', color: '#0f172a' }}>{e.patient_name || '—'}</div>
                        <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>{e.comment}</div>
                        <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>{e.feedback_date ? String(e.feedback_date).split('-').reverse().join('-') : ''}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {visibleFeedback.length === 0 && (
              <div style={{ gridColumn: '1/-1', padding: '3rem', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                {fq ? `No feedback matches client “${fbSearch}”.` : <>No feedback yet — click <strong>+ Add Feedback</strong> to record client feedback for a staff member.</>}
              </div>
            )}
          </div>

          {/* Add Feedback modal */}
          {fbModal && (
            <div onClick={() => setFbModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '14px', width: '440px', maxWidth: '92vw', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#fafafa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Add Feedback</div>
                  <button onClick={() => setFbModal(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '20px', color: '#94a3b8', lineHeight: 1 }}>×</button>
                </div>
                <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {(['nurse', 'physio'] as const).map(t => (
                      <button key={t} onClick={() => setFbForm(f => ({ ...f, staff_type: t, staff_id: '' }))}
                        style={{ flex: 1, padding: '7px', borderRadius: '8px', border: '1px solid', cursor: 'pointer', fontSize: '12px', fontWeight: 600, textTransform: 'capitalize',
                          borderColor: fbForm.staff_type === t ? '#2563eb' : '#e2e8f0', background: fbForm.staff_type === t ? '#2563eb' : '#fff', color: fbForm.staff_type === t ? '#fff' : '#64748b' }}>
                        {t}
                      </button>
                    ))}
                  </div>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Staff member
                    <SearchableSelect
                      value={fbForm.staff_id}
                      display={(fbForm.staff_type === 'physio' ? physios : nurses).find((s: any) => String(s.id) === fbForm.staff_id)?.full_name || ''}
                      placeholder="Search staff by name…"
                      options={(fbForm.staff_type === 'physio' ? physios : nurses).map((s: any) => ({ id: String(s.id), label: s.full_name || `#${s.id}` }))}
                      onSelect={(id) => setFbForm(f => ({ ...f, staff_id: id }))}
                    />
                  </label>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Case / Client <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span>
                    <SearchableSelect
                      value={fbForm.patient_id}
                      display={(() => { const p = fbAllPatients.find((x: any) => String(x.id) === fbForm.patient_id); return p ? (getClientName(p) || p.patient_name || '') : ''; })()}
                      placeholder="Search by client or patient…"
                      options={fbAllPatients.map((p: any) => ({ id: String(p.id), label: getClientName(p) || p.patient_name || `#${p.id}`, sub: getClientName(p) ? p.patient_name : undefined }))}
                      onSelect={(id) => setFbForm(f => ({ ...f, patient_id: id }))}
                      allowClear
                    />
                  </label>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Comment
                    <textarea value={fbForm.comment} onChange={(e) => setFbForm(f => ({ ...f, comment: e.target.value }))} rows={3} placeholder="e.g. Excellent critical care, very attentive" style={{ ...fbInput, resize: 'vertical' }} />
                  </label>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Date
                    <input type="date" value={fbForm.feedback_date} max={todayStr()} onChange={(e) => setFbForm(f => ({ ...f, feedback_date: e.target.value }))} style={fbInput} />
                  </label>
                </div>
                <div style={{ padding: '12px 18px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                  <button onClick={() => setFbModal(false)} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={submitFeedback} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>Save Feedback</button>
                </div>
              </div>
            </div>
          )}
        </div>
        );
      })()}

      {activeTab === 'log' && (
        <div style={{ padding: '12px 20px' }}>
          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', color: '#1d2631' }}>
              <thead>
                <tr>
                  {['Case / Patient', 'Shift Type', 'Session', 'Assigned Nurse / Physio', 'Secondary Staff', 'Start Date', ''].map(hd => (
                    <th key={hd} style={{ padding: '0.55rem 0.75rem', background: '#86afd8', color: '#475569', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', border: '1px solid #c5cbd3', textAlign: 'left' }}>{hd}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {assignments.length === 0 && <tr><td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>No assignments for {selectedDate}</td></tr>}
                {assignments.map(a => {
                  const pid = a.patient_id ?? a.patient?.id;
                  const pat = patients.find(p => p.id === pid);
                  return (
                    <tr key={a.id}
                      onMouseEnter={e => { Array.from(e.currentTarget.cells).forEach(c => (c.style.background = '#f8fafc')); }}
                      onMouseLeave={e => { Array.from(e.currentTarget.cells).forEach(c => (c.style.background = '')); }}>
                      <td style={{ padding: '0.55rem 0.75rem', fontWeight: 700, color: '#0f172a', fontSize: '12px', border: '1px solid #c5cbd3' }}>
                        {pat ? pat.patient_name : a.patient?.patient_name || `Patient #${pid}`}
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem', border: '1px solid #c5cbd3' }}>
                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '4px', background: '#f1f5f9', color: '#475569' }}>{a.shift_type}</span>
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem', fontSize: '11px', color: '#64748b', border: '1px solid #c5cbd3' }}>{a.session || '—'}</td>
                      <td style={{ padding: '0.55rem 0.75rem', fontSize: '12px', color: '#16a34a', fontWeight: 600, border: '1px solid #c5cbd3' }}>{a.nurse?.full_name || a.physio?.full_name || <span style={{ color: '#dc2626' }}>— Unassigned</span>}</td>
                      <td style={{ padding: '0.55rem 0.75rem', fontSize: '12px', color: '#334155', border: '1px solid #c5cbd3' }}>{a.second_nurse?.full_name || a.second_physio?.full_name || '—'}</td>
                      <td style={{ padding: '0.55rem 0.75rem', fontSize: '11px', color: '#64748b', fontFamily: 'monospace', border: '1px solid #c5cbd3' }}>{a.start_date}</td>
                      <td style={{ padding: '0.55rem 0.75rem', border: '1px solid #c5cbd3' }}>
                        <button onClick={() => pat && handleAssignClick(pat)}
                          style={{ fontSize: '10px', fontWeight: 700, padding: '3px 9px', borderRadius: '6px', background: 'linear-gradient(135deg,#0ea5e9 0%,#2563eb 100%)', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Add case modal ── */}
      {addingCase && renderAddCaseModal()}

      {/* ── Case note modal ── */}
      {notePatient && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 3200, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}
          onClick={() => setNotePatient(null)}>
          <div style={{ background: '#fff', borderRadius: '14px', width: '460px', maxWidth: '92vw', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Case Note</div>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                  {getClientName(notePatient) || notePatient.patient_name}{noteDate ? ` · ${fmtDateShort(noteDate)}` : ''}
                </div>
              </div>
              <button onClick={() => setNotePatient(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '20px', lineHeight: 1, width: '28px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <div style={{ padding: '16px 18px' }}>
              <textarea autoFocus value={noteDraft} onChange={e => setNoteDraft(e.target.value)}
                placeholder="Write a note about this case…"
                rows={6}
                style={{ width: '100%', padding: '10px 12px', border: `1px solid ${NOTE_COLORS[noteColorDraft] || NOTE_COLORS.black}`, borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit', outline: 'none', color: NOTE_COLORS[noteColorDraft] || '#0f172a', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 }} />
              {/* Color picker for the note view-box */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Color</span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {(['black', 'blue', 'green', 'red', 'yellow'] as const).map(k => {
                    const c = NOTE_COLORS[k];
                    const sel = noteColorDraft === k;
                    return (
                      <button key={k} onClick={() => setNoteColorDraft(k)} title={k}
                        style={{ width: '24px', height: '24px', borderRadius: '50%', background: c, cursor: 'pointer', padding: 0,
                          border: sel ? '2px solid #fff' : '2px solid #fff', boxShadow: sel ? `0 0 0 2px ${c}` : 'inset 0 0 0 1px rgba(0,0,0,0.1)' }} />
                    );
                  })}
                </div>
              </div>
            </div>
            <div style={{ padding: '12px 18px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setNotePatient(null)}
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={saveNote} disabled={noteSaving}
                style={{ padding: '8px 18px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#3b82f6 0%,#2563eb 100%)', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: noteSaving ? 'default' : 'pointer', fontFamily: 'inherit', opacity: noteSaving ? 0.7 : 1 }}>
                {noteSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Available Today assign picker modal ── */}
      {availModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}
          onClick={() => setAvailModal(false)}>
          <div style={{ background: '#fff', borderRadius: '14px', width: '380px', maxWidth: '92vw', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Assign to a Case</div>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                  {availNurse?.name} · Select patient and shift
                </div>
              </div>
              <button onClick={() => setAvailModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '20px', lineHeight: 1, width: '28px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Case picker — search by CLIENT name (primary), patient name (secondary) */}
              <div>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748b', marginBottom: '5px' }}>Client / Case</label>
                {!availPatientId ? (
                  <>
                    <input autoFocus value={availClientSearch} onChange={e => setAvailClientSearch(e.target.value)}
                      placeholder="Search client name, or patient name…"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px', fontFamily: 'inherit', outline: 'none', color: '#0f172a', background: '#fff', boxSizing: 'border-box' }} />
                    {(() => {
                      const q = availClientSearch.trim().toLowerCase();
                      const list = patients
                        .map(p => ({ p, client: getClientName(p), name: p.patient_name || '' }))
                        .filter(x => !q || x.client.toLowerCase().includes(q) || x.name.toLowerCase().includes(q))
                        .sort((a, b) => {
                          // duty-type order first (08 → 12 Day → 12 Night → 12 Dual → 24)
                          const dr = dutyRank(a.p) - dutyRank(b.p);
                          if (dr !== 0) return dr;
                          // client-name hits rank above patient-only hits
                          const ac = q && a.client.toLowerCase().includes(q) ? 0 : 1;
                          const bc = q && b.client.toLowerCase().includes(q) ? 0 : 1;
                          if (ac !== bc) return ac - bc;
                          const cc = a.client.localeCompare(b.client);
                          return cc !== 0 ? cc : a.name.localeCompare(b.name);
                        })
                        .slice(0, 60);
                      return (
                        <div style={{ marginTop: '6px', maxHeight: '210px', overflowY: 'auto', border: '1px solid #f1f5f9', borderRadius: '8px' }}>
                          {list.length === 0
                            ? <div style={{ padding: '14px', fontSize: '11px', color: '#94a3b8', textAlign: 'center' }}>No matching cases</div>
                            : list.map(({ p, client, name }) => (
                              <div key={p.id} onClick={() => { setAvailPatientId(String(p.id)); setAvailShift(null); }}
                                style={{ padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid #f8fafc', display: 'flex', alignItems: 'center', gap: '8px' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No client</span>}</div>
                                  <div style={{ fontSize: '10px', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name} · {getDutyLabel(p)}</div>
                                </div>
                              </div>
                            ))}
                        </div>
                      );
                    })()}
                  </>
                ) : (() => {
                  const pat = patients.find(p => String(p.id) === availPatientId);
                  if (!pat) return null;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc' }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getClientName(pat) || pat.patient_name}</div>
                        <div style={{ fontSize: '10px', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pat.patient_name} · {getDutyLabel(pat)}</div>
                      </div>
                      <button onClick={() => { setAvailPatientId(''); setAvailShift(null); }}
                        style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', color: '#64748b', fontSize: '10px', fontWeight: 700, padding: '4px 8px', flexShrink: 0 }}>Change</button>
                    </div>
                  );
                })()}
              </div>

              {/* Shift selector — only OPEN (unstaffed) shifts the case actually has */}
              {availPatientId && (() => {
                const pat = patients.find(p => String(p.id) === availPatientId);
                if (!pat) return null;
                const openShifts = getShiftsForPatient(pat, activeAssignments).filter(s => s.names.length === 0);
                return (
                  <div>
                    <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748b', marginBottom: '5px' }}>Available Shift · {fmtDateShort(activeDate)}</label>
                    {openShifts.length === 0 ? (
                      <div style={{ padding: '10px', fontSize: '11px', color: '#94a3b8', textAlign: 'center', border: '1px dashed #e2e8f0', borderRadius: '8px' }}>
                        No open shifts for this case on this day.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {openShifts.map(s => {
                          const sel = availShift?.session === s.session;
                          return (
                            <button key={s.shiftId} onClick={() => setAvailShift({ label: s.label, session: s.session })}
                              style={{ padding: '7px 12px', borderRadius: '7px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                                border: sel ? '1.5px solid #16a34a' : '1.5px solid #e2e8f0', background: sel ? '#f0fdf4' : '#fff', color: sel ? '#15803d' : '#334155' }}>
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
              <button onClick={confirmAvailAssign} disabled={!availPatientId || !availShift}
                style={{ width: '100%', padding: '10px', background: availPatientId && availShift ? 'linear-gradient(135deg,#22c55e 0%,#16a34a 100%)' : '#e2e8f0', color: availPatientId && availShift ? '#fff' : '#94a3b8', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: availPatientId && availShift ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
                Continue to Assignment ›
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Staff Pool → nurse profile ── */}
      <ViewNurseModal isOpen={viewNurseId != null} onClose={() => setViewNurseId(null)} nurseId={viewNurseId} />

      {/* ── Add Outsourcing modal ── */}
      {outModal && createPortal(
        <div onClick={() => setOutModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 4100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '10vh', backdropFilter: 'blur(2px)' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '14px', width: '440px', maxWidth: '94vw', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', padding: '20px 22px', fontFamily: 'inherit' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: 800, color: '#be123c', margin: 0 }}>Add Outsourcing</h2>
              <button onClick={() => setOutModal(false)} style={{ fontSize: '18px', lineHeight: 1, color: '#64748b', background: '#f1f5f9', border: 'none', borderRadius: '8px', width: '30px', height: '28px', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '14px' }}>{fmtDateLong(activeDate)} · the rate is subtracted from that day's sales</div>

            {/* Client name — pick from clients or type your own */}
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', marginBottom: '4px' }}>Client name</label>
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <input
                value={outForm.client_name}
                onChange={e => { setOutForm(f => ({ ...f, client_name: e.target.value })); setOutClientOpen(true); }}
                onFocus={() => setOutClientOpen(true)}
                placeholder="Choose a client or type a name…"
                style={{ width: '100%', padding: '9px 11px', fontSize: '13px', border: '1px solid #e2e8f0', borderRadius: '8px', boxSizing: 'border-box', fontFamily: 'inherit' }}
              />
              {outClientOpen && (() => {
                const q = outForm.client_name.trim().toLowerCase();
                const matches = allPatients
                  .filter(p => { const n = getClientName(p) || p.patient_name || ''; return !q || n.toLowerCase().includes(q) || (p.patient_name || '').toLowerCase().includes(q); })
                  .slice(0, 8);
                if (matches.length === 0) return null;
                return (
                  <div style={{ position: 'absolute', top: '40px', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 5, maxHeight: '220px', overflowY: 'auto' }}>
                    {matches.map(p => {
                      const nm = getClientName(p) || p.patient_name;
                      return (
                        <div key={p.id} onClick={() => { setOutForm(f => ({ ...f, client_name: nm })); setOutClientOpen(false); }}
                          style={{ padding: '8px 11px', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                          onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
                          <span style={{ fontWeight: 700, color: '#0f172a' }}>{nm}</span>
                          {getClientName(p) && <span style={{ color: '#94a3b8', marginLeft: '6px' }}>· {p.patient_name}</span>}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Note */}
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', marginBottom: '4px' }}>Note <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
            <textarea
              value={outForm.note}
              onChange={e => setOutForm(f => ({ ...f, note: e.target.value }))}
              placeholder="Reason / details…" rows={2}
              style={{ width: '100%', padding: '9px 11px', fontSize: '13px', border: '1px solid #e2e8f0', borderRadius: '8px', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical', marginBottom: '12px' }}
            />

            {/* Rate */}
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', marginBottom: '4px' }}>Rate (₹)</label>
            <input
              type="number" min="0" step="0.01"
              value={outForm.rate}
              onChange={e => setOutForm(f => ({ ...f, rate: e.target.value }))}
              placeholder="0.00"
              style={{ width: '100%', padding: '9px 11px', fontSize: '13px', border: '1px solid #e2e8f0', borderRadius: '8px', boxSizing: 'border-box', fontFamily: 'inherit', textAlign: 'right', marginBottom: '16px' }}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setOutModal(false)}
                style={{ fontSize: '12px', fontWeight: 700, color: '#475569', background: '#f1f5f9', border: 'none', borderRadius: '8px', padding: '9px 14px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={saveOutsourcing}
                style={{ fontSize: '12px', fontWeight: 700, color: '#fff', background: '#e11d48', border: 'none', borderRadius: '8px', padding: '9px 16px', cursor: 'pointer', fontFamily: 'inherit' }}>Save Outsourcing</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Daily Sales month chart ── */}
      {salesModalOpen && createPortal(
        <div onClick={() => setSalesModalOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '14px', width: 'min(1040px, 96vw)', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', padding: '20px 22px', fontFamily: 'inherit' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '6px' }}>
              <div>
                <h2 style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a', margin: 0 }}>Daily Sales — {fmtMonthYear(selectedDate)}</h2>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>Client charge for assigned shifts (primary + assistant), each day of the month</div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <button onClick={downloadSalesChart} disabled={salesLoading || salesData.length === 0}
                  style={{ fontSize: '12px', fontWeight: 700, color: '#fff', background: (salesLoading || salesData.length === 0) ? '#cbd5e1' : '#0d9488', border: 'none', borderRadius: '8px', padding: '7px 12px', cursor: (salesLoading || salesData.length === 0) ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                  ⬇ Download PNG
                </button>
                <button onClick={() => setSalesModalOpen(false)}
                  style={{ fontSize: '18px', lineHeight: 1, color: '#64748b', background: '#f1f5f9', border: 'none', borderRadius: '8px', width: '32px', cursor: 'pointer' }}>×</button>
              </div>
            </div>

            {salesLoading ? (
              <div style={{ padding: '5rem', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>Loading daily sales…</div>
            ) : salesData.length === 0 ? (
              <div style={{ padding: '5rem', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No sales data for this month.</div>
            ) : (
              <>
                <div style={{ fontSize: '12px', color: '#475569', margin: '10px 0 4px' }}>
                  Month total: <strong style={{ color: '#0d9488' }}>₹{salesData.reduce((s, d) => s + d.sales, 0).toLocaleString('en-IN')}</strong>
                </div>
                <div ref={salesChartRef} style={{ width: '100%', height: 460, background: '#fff' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={salesData} margin={{ top: 16, right: 20, left: 10, bottom: 46 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} angle={-45} textAnchor="end" height={64} interval={0} tickLine={false} />
                      <YAxis tickFormatter={(v: number) => v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : v >= 1000 ? `₹${(v / 1000).toFixed(0)}K` : `₹${v}`} tick={{ fontSize: 11, fill: '#94a3b8' }} width={72} axisLine={false} tickLine={false} />
                      <RTooltip formatter={(v: any) => [`₹${Number(v).toLocaleString('en-IN')}`, 'Daily Sale']} labelStyle={{ fontWeight: 700, color: '#0f172a' }} />
                      <Bar dataKey="sales" name="Daily Sale" fill="#0d9488" radius={[3, 3, 0, 0]} maxBarSize={30} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* ── AssignNurseModal — passes initialSlots and preNurseId when coming from daily board ── */}
      <AssignNurseModal
        isOpen={isAssignModalOpen}
        onClose={() => { setIsAssignModalOpen(false); setInitialSlots([]); setPreNurseId(undefined); fetchAll(); if (activeTab === 'upcoming') fetchUpcomingAsgn(); }}
        onSuccess={() => { setIsAssignModalOpen(false); setInitialSlots([]); setPreNurseId(undefined); fetchAll(); if (activeTab === 'upcoming') fetchUpcomingAsgn(); }}
        patient={selectedPatient}
        nurses={nurses}
        physios={physios}
        selectedDate={selectedDate}
        initialSlots={initialSlots}
        preNurseId={preNurseId}
        blockedStaff={blockedStaffMap}
        onOverrideAvailability={overrideAvailabilityToAvailable}
      />

      {/* Quick-add nurse (from the Leave Tracker → New Joinee column). On success the
          new nurse is stamped "New Joinee" for the active day so they land in that column. */}
      <AddNurseModal
        isOpen={isAddNurseOpen}
        onClose={() => setIsAddNurseOpen(false)}
        onSuccess={async (createdNurse?: any) => {
          setIsAddNurseOpen(false);
          const nid = createdNurse?.id;
          if (nid) {
            try {
              await axios.post(`${API}/api/nurse-availability`,
                { nurse_id: Number(nid), date: availDate, category: 'Available' }, { headers: h });
            } catch (e) { console.error('mark availability', e); }
          }
          fetchAll();
          fetchAvailability();
          fetchLeavePeriods();
        }}
      />
    </div>
  );
};

export default OperationsDashboard;
